import { UIMetadataComplete } from '../../../types/UIMetadata';

/**
 * Cadillac Lyriq (linkId 2369).
 *
 * UI half of the 2.0 metadata pair — every key mirrors a configurationId in
 * ./graphics-metadata.ts. Converted from the 1.0 repo's
 * `src/metadata/data/metadata-2369.ts`; shared UI boilerplate
 * (staticTranslations / dealers / saveFormConfig / externalScript) follows
 * 2378. `styleConfig` reuses the Cadillac Gothic palette/face verbatim from 2384.
 *
 * PARTIAL ASSET GAP: every per-entity image (colours, features) for the
 * 2025 Luxury and 2025 Sport trims is missing at the deployed 1.0 host
 * under any extension - 43 of 71 references probed individually - while
 * 2026 LYRIQ-V's entire set resolves cleanly (28 files). Missing image
 * fields are left as empty strings, same treatment as Yukon (2368).
 * 
 * 2025 Sport and 2026 LYRIQ-V share most configurationIds (7 colours, 9
 * features, 7 interactions) but differ in content (different swatch/still
 * filenames - one broken, one real), so they correctly split into
 * separate `-2026-lyriq-v`-suffixed entries rather than merging.
 *
 * 1.0 ships a REAL `modelyear` filter here (2025/2026, 2026 checked
 * by default) - the same mechanism as Tahoe's existing modelyear filter.
 * Kept, and CONFIG is left visible because of it.
 *
 * Assets live in public/assets/{images,videos}/2369/, resolved by
 * `assetSrc()` in src/utils/metadata.ts.
 */
// Cast through unknown to allow additional model-level UI fields (e.g., name, specifications)
export const uimetadata = {
  models: {
    Lyriq: {
      variants: {
        '2025-luxury': {
          configurationId: '5vlpab0glq63',
          name: {
            en: '2025 Luxury',
            ar: 'فئة الفخامة 2025',
          },
          description: {
            en: 'AWD',
            ar: 'دفع رباعي',
          },
          drivetrain: 'AWD',
          trim: '2025 Luxury',
          highlights: [
            {
              en: '515 HP',
              ar: '515 حصان',
            },
            {
              en: 'Torque 610 NM',
              ar: 'عزم الدوران 610 نيوتن متر',
            },
            {
              en: 'Range 463 km',
              ar: 'المدى 463 كم',
            },
          ],
          specs: [
            {
              en: '515 HP',
              ar: '515 حصان',
            },
            {
              en: '610 NM',
              ar: '610 نيوتن متر',
            },
          ],
          image: 'images/2369/Luxurycatalogue.png',
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          basePrice: {
            UAE: 0,
            KSA: 0,
            KW: 0,
            default: 0.1,
          },
          index: 0,
          isDefault: false,
          ar: {
            usdz: '',
            enable: false,
          },
          modelyear: '2025',
          toggleTabs: {
            CONFIG: {
              mobile: true,
              desktop: true,
            },
            MODEL: {
              mobile: true,
              desktop: true,
            },
            EXTERIOR: {
              mobile: true,
              desktop: true,
            },
            INTERIOR: {
              mobile: true,
              desktop: true,
            },
            OPTIONS: {
              mobile: false,
              desktop: false,
            },
            SUMMARY: {
              mobile: true,
              desktop: true,
            },
          },
          autoIntelRPOCode: '1SE',
          brandItemCode: 'CALY01',
        },
        '2025-sport': {
          configurationId: '5vlpab0glq64',
          name: {
            en: '2025 Sport',
            ar: 'SPORT موديل 2025',
          },
          description: {
            en: 'AWD',
            ar: 'دفع رباعي',
          },
          drivetrain: 'AWD',
          trim: '2025 Sport',
          highlights: [
            {
              en: '515 HP',
              ar: '515 حصان',
            },
            {
              en: 'Torque 610 NM',
              ar: 'عزم الدوران 610 نيوتن متر',
            },
            {
              en: 'Range 463 km',
              ar: 'المدى 463 كم',
            },
          ],
          specs: [
            {
              en: '515 HP',
              ar: '515 حصان',
            },
            {
              en: '610 NM',
              ar: '610 نيوتن متر',
            },
          ],
          image: 'images/2369/sportcatalogue.png',
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          basePrice: {
            UAE: 0,
            KSA: 0,
            KW: 0,
            default: 0.1,
          },
          index: 1,
          isDefault: false,
          ar: {
            usdz: '',
            enable: false,
          },
          modelyear: '2025',
          toggleTabs: {
            CONFIG: {
              mobile: true,
              desktop: true,
            },
            MODEL: {
              mobile: true,
              desktop: true,
            },
            EXTERIOR: {
              mobile: true,
              desktop: true,
            },
            INTERIOR: {
              mobile: true,
              desktop: true,
            },
            OPTIONS: {
              mobile: false,
              desktop: false,
            },
            SUMMARY: {
              mobile: true,
              desktop: true,
            },
          },
          autoIntelRPOCode: '1SL',
          brandItemCode: 'CALY02',
        },
        '2026-lyriq-v': {
          configurationId: '5vlpabvyas0glq64',
          name: {
            en: '2026 LYRIQ-V',
            ar: 'LYRIQ-V موديل 2026',
          },
          description: {
            en: 'AWD',
            ar: 'دفع رباعي',
          },
          drivetrain: 'AWD',
          trim: '2026 LYRIQ-V',
          highlights: [
            {
              en: '515 HP',
              ar: '515 حصان',
            },
            {
              en: 'Torque 610 NM',
              ar: 'عزم الدوران 610 نيوتن متر',
            },
            {
              en: 'Range 463 km',
              ar: 'المدى 463 كم',
            },
          ],
          specs: [
            {
              en: '515 HP',
              ar: '515 حصان',
            },
            {
              en: '610 NM',
              ar: '610 نيوتن متر',
            },
          ],
          image: 'images/2369/lyriq_v_variant.png',
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          basePrice: {
            UAE: 0,
            KSA: 0,
            KW: 0,
            default: 0.1,
          },
          index: 2,
          isDefault: true,
          ar: {
            usdz: '',
            enable: false,
          },
          modelyear: '2026',
          toggleTabs: {
            CONFIG: {
              mobile: true,
              desktop: true,
            },
            MODEL: {
              mobile: true,
              desktop: true,
            },
            EXTERIOR: {
              mobile: true,
              desktop: true,
            },
            INTERIOR: {
              mobile: true,
              desktop: true,
            },
            OPTIONS: {
              mobile: false,
              desktop: false,
            },
            SUMMARY: {
              mobile: true,
              desktop: true,
            },
          },
          autoIntelRPOCode: '1SM',
          brandItemCode: 'CALY04',
        },
      },
      backgrounds: {
        g0ZA968_RpUd: {
          image: 'images/2369/ext_colors/official/Argent_Silver_Metallic.jpg',
          title: {
            en: 'City Landscape 1',
            ar: 'غروب الشمس على الشاطئ',
          },
        },
        g0ZA968_RpUe: {
          image: 'images/2369/change_bg_2.webp',
          title: {
            en: 'City Landscape 2',
            ar: 'غروب الشمس على الشاطئ',
          },
        },
      },
      ambientLighting: {},
      colors: {
        exterior: {
          '4f9dd3vca75z': {
            label: {
              en: 'Argent Silver Metallic',
              ar: 'فضي Argent معدني',
            },
            image: 'images/2369/ext_colors/official/Argent_Silver_Metallic.jpg',
            usdz: 'black',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              LBP: 0,
              default: 0,
            },
            index: 0,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2369/ext_colors/official/Argent_Silver_Metallic.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'GXD',
            brandItemCode: 'EX26',
          },
          '69xux2hlscm1': {
            label: {
              en: 'Celestial Metallic',
              ar: 'Celestial معدني',
            },
            image: 'images/2369/ext_colors/official/Celestial_Metallic.jpg',
            usdz: 'silver',
            price: {
              KSA: 3078,
              UAE: 3021,
              OMR: 320,
              BHD: 312,
              QAR: 2988,
              JOD: 583,
              KW: 254,
              LBP: 821,
              IQD: 821,
              default: 3021,
            },
            index: 1,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2369/ext_colors/official/Celestial_Metallic.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'GJY',
            brandItemCode: 'EX25',
          },
          '0pazineb3kjj': {
            label: {
              en: 'Crystal White Tricoat',
              ar: 'أبيض Crystal بثلاث طبقات',
            },
            image: 'images/2369/ext_colors/official/Crystal_White_Tricoat.jpg',
            usdz: 'red',
            price: {
              KSA: 5449,
              UAE: 5347,
              OMR: 567,
              BHD: 552,
              QAR: 5289,
              JOD: 1032,
              KW: 450,
              LBP: 1453,
              IQD: 1453,
              default: 5347,
            },
            index: 2,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2369/ext_colors/official/Crystal_White_Tricoat.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'G1W',
            brandItemCode: 'EX22',
          },
          qzewltf6e2pf: {
            label: {
              en: 'Radiant Red Tintcoat',
              ar: 'أحمر Radiant بطبقتين',
            },
            image: 'images/2369/ext_colors/official/Radiant_Red_Tintcoat.jpg',
            usdz: 'white',
            price: {
              KSA: 5449,
              UAE: 5347,
              OMR: 567,
              BHD: 552,
              QAR: 5289,
              JOD: 1032,
              KW: 450,
              LBP: 1453,
              IQD: 1453,
              default: 5347,
            },
            index: 3,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2369/ext_colors/official/Radiant_Red_Tintcoat.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'GNT',
            brandItemCode: 'EX03',
          },
          '10xy9i682nxc': {
            label: {
              en: 'Emerald Lake Metallic',
              ar: 'أزرق Emerald Lake معدني',
            },
            image: 'images/2369/ext_colors/official/Emerald_Lake_Metallic.jpg',
            usdz: 'blue',
            price: {
              KSA: 3078,
              UAE: 3021,
              OMR: 320,
              BHD: 312,
              QAR: 2988,
              JOD: 583,
              KW: 254,
              LBP: 821,
              IQD: 821,
              default: 3021,
            },
            index: 4,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2369/ext_colors/official/Emerald_Lake_Metallic.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'G6I',
            brandItemCode: 'EX27',
          },
          '37wp0lpjh309': {
            label: {
              en: 'Stellar Black Metallic',
              ar: 'أسود Stellar معدني',
            },
            image: 'images/2369/ext_colors/official/Stellar_Black_Metallic.jpg',
            usdz: 'red_black',
            price: {
              KSA: 3078,
              UAE: 3021,
              OMR: 320,
              BHD: 312,
              QAR: 2988,
              JOD: 583,
              KW: 254,
              LBP: 821,
              IQD: 821,
              default: 3021,
            },
            index: 5,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2369/ext_colors/official/Stellar_Black_Metallic.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'GB8',
            brandItemCode: 'EX28',
          },
          wf9o2nslk4du: {
            label: {
              en: 'Argent Silver Metallic with Black roof',
              ar: 'فضي Argent معدني مع سقف أسود',
            },
            image: 'images/2369/ext_colors/official/Argent_Silver_Metallic.jpg',
            usdz: 'silver_black',
            price: {
              KSA: 2590,
              UAE: 2542,
              OMR: 269,
              BHD: 262,
              QAR: 2514,
              JOD: 490,
              KW: 214,
              LBP: 691,
              IQD: 691,
              default: 2542,
            },
            index: 6,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2369/ext_colors/official/Argent_Silver_Metallic.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'GXD',
            brandItemCode: 'EX57',
          },
          '8nckjcklkf2a': {
            label: {
              en: 'Celestial Metallic with Black roof',
              ar: 'Celestial معدني مع سقف أسود',
            },
            image: 'images/2369/ext_colors/official/Celestial_Metallic.jpg',
            usdz: 'blue_black',
            price: {
              KSA: 5669,
              UAE: 5563,
              OMR: 590,
              BHD: 574,
              QAR: 5503,
              JOD: 1073,
              KW: 469,
              LBP: 1512,
              IQD: 1512,
              default: 5563,
            },
            index: 7,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2369/ext_colors/official/Celestial_Metallic.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'GJY',
            brandItemCode: 'EX61',
          },
          '4zd4onnmg2y8': {
            label: {
              en: 'Crystal White Tricoat with Black roof',
              ar: 'أبيض Crystal بثلاث طبقات مع سقف أسود',
            },
            image: 'images/2369/ext_colors/official/Crystal_White_Tricoat.jpg',
            usdz: 'blue_black',
            price: {
              KSA: 8039,
              UAE: 7889,
              OMR: 836,
              BHD: 815,
              QAR: 7803,
              JOD: 1522,
              KW: 665,
              LBP: 2144,
              IQD: 2144,
              default: 7889,
            },
            index: 8,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2369/ext_colors/official/Crystal_White_Tricoat.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'G1W',
            brandItemCode: 'EX66',
          },
          '4zodlcbujnve': {
            label: {
              en: 'Radiant Red Tintcoat with Black roof',
              ar: 'أحمر Radiant بطبقتين مع سقف أسود',
            },
            image: 'images/2369/ext_colors/official/Radiant_Red_Tintcoat.jpg',
            usdz: 'white_black',
            price: {
              KSA: 8039,
              UAE: 7889,
              OMR: 836,
              BHD: 815,
              QAR: 7803,
              JOD: 1522,
              KW: 665,
              LBP: 2144,
              IQD: 2144,
              default: 7889,
            },
            index: 9,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2369/ext_colors/official/Radiant_Red_Tintcoat.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'GNT',
            brandItemCode: 'EX86',
          },
          '03495u2095u209': {
            label: {
              en: 'Emerald Lake Metallic with Black roof',
              ar: 'أزرق Emerald Lake معدني مع سقف أسود',
            },
            image: 'images/2369/ext_colors/official/Emerald_Lake_Metallic.jpg',
            usdz: 'white_black',
            price: {
              KSA: 5669,
              UAE: 5563,
              OMR: 590,
              BHD: 574,
              QAR: 5503,
              JOD: 1073,
              KW: 469,
              LBP: 1512,
              IQD: 1512,
              default: 5563,
            },
            index: 10,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2369/ext_colors/official/Emerald_Lake_Metallic.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'G6I',
            brandItemCode: 'EX74',
          },
          '2fa06p9t7v8f': {
            label: {
              en: 'Argent Silver Metallic',
              ar: 'فضي Argent معدني',
            },
            image: 'images/2369/ext_colors/official/Argent_Silver_Metallic.jpg',
            usdz: 'black',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              default: 0,
            },
            index: 11,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2369/ext_colors/official/Argent_Silver_Metallic.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'GXD',
            brandItemCode: 'EX26',
          },
          '2fa06p9t7v8f-2026-lyriq-v': {
            label: {
              en: 'Vibrant White Tricoat',
              ar: 'أبيض Vibrant بثلاث طبقات',
            },
            image: 'images/2369/ext_colors/official/Vibrant_White_Tricoat.jpg',
            usdz: 'black',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              default: 0,
            },
            index: 12,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2369/ext_colors/official/Vibrant_White_Tricoat.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'G4J',
            brandItemCode: 'EX97',
          },
          '5xzh26yrdinw': {
            label: {
              en: 'Crystal White Tricoat',
              ar: 'أبيض Crystal بثلاث طبقات',
            },
            image: 'images/2369/ext_colors/official/Crystal_White_Tricoat.jpg',
            usdz: 'red',
            price: {
              KSA: 5449,
              UAE: 5347,
              OMR: 567,
              BHD: 552,
              QAR: 5289,
              JOD: 1032,
              KW: 450,
              LBP: 1453,
              IQD: 1453,
              default: 5347,
            },
            index: 13,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2369/ext_colors/official/Crystal_White_Tricoat.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'G1W',
            brandItemCode: 'EX22',
          },
          '5xzh26yrdinw-2026-lyriq-v': {
            label: {
              en: 'Emerald Lake Metallic',
              ar: 'أزرق Emerald Lake معدني',
            },
            image: 'images/2369/ext_colors/official/Emerald_Lake_Metallic.jpg',
            usdz: 'red',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              default: 0,
            },
            index: 14,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2369/ext_colors/official/Emerald_Lake_Metallic.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'G6I',
            brandItemCode: 'EX27',
          },
          '2fbmj48vpgsi': {
            label: {
              en: 'Radiant Red Tintcoat',
              ar: 'أحمر Radiant بطبقتين',
            },
            image: 'images/2369/ext_colors/official/Radiant_Red_Tintcoat.jpg',
            usdz: 'white',
            price: {
              KSA: 5449,
              UAE: 5347,
              OMR: 567,
              BHD: 552,
              QAR: 5289,
              JOD: 1032,
              KW: 450,
              LBP: 1453,
              IQD: 1453,
              default: 5347,
            },
            index: 15,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2369/ext_colors/official/Radiant_Red_Tintcoat.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'GNT',
            brandItemCode: 'EX03',
          },
          '2fbmj48vpgsi-2026-lyriq-v': {
            label: {
              en: 'Nimbus Metallic',
              ar: 'Nimbus معدني',
            },
            image: 'images/2369/ext_colors/official/Nimbus_Metallic.jpg',
            usdz: 'white',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              default: 0,
            },
            index: 16,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2369/ext_colors/official/Nimbus_Metallic.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'G7X',
            brandItemCode: 'EX82',
          },
          '37xytt0490j8': {
            label: {
              en: 'Stellar Black Metallic',
              ar: 'أسود Stellar معدني',
            },
            image: 'images/2369/ext_colors/official/Stellar_Black_Metallic.jpg',
            usdz: 'red_black',
            price: {
              KSA: 3078,
              UAE: 3021,
              OMR: 320,
              BHD: 312,
              QAR: 2988,
              JOD: 583,
              KW: 254,
              LBP: 821,
              IQD: 821,
              default: 3021,
            },
            index: 17,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2369/ext_colors/official/Stellar_Black_Metallic.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'GB8',
            brandItemCode: 'EX28',
          },
          '37xytt0490j8-2026-lyriq-v': {
            label: {
              en: 'Radiant Red Tintcoat',
              ar: 'أحمر Radiant بطبقتين',
            },
            image: 'images/2369/ext_colors/official/Radiant_Red_Tintcoat.jpg',
            usdz: 'red_black',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              default: 0,
            },
            index: 18,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2369/ext_colors/official/Radiant_Red_Tintcoat.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'GNT',
            brandItemCode: 'EX03',
          },
          '536y2x49kxgq': {
            label: {
              en: 'Argent Silver Metallic with Black roof',
              ar: 'فضي Argent معدني مع سقف أسود',
            },
            image: 'images/2369/ext_colors/official/Argent_Silver_Metallic.jpg',
            usdz: 'silver_black',
            price: {
              KSA: 2590,
              UAE: 2542,
              OMR: 269,
              BHD: 262,
              QAR: 2514,
              JOD: 490,
              KW: 214,
              LBP: 691,
              IQD: 691,
              default: 2542,
            },
            index: 19,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2369/ext_colors/official/Argent_Silver_Metallic.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'GXD',
            brandItemCode: 'EX57',
          },
          '536y2x49kxgq-2026-lyriq-v': {
            label: {
              en: 'Argent Silver Metallic',
              ar: 'فضي Argent معدني',
            },
            image: 'images/2369/ext_colors/official/Argent_Silver_Metallic.jpg',
            usdz: 'silver_black',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              default: 0,
            },
            index: 20,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2369/ext_colors/official/Argent_Silver_Metallic.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'GXD',
            brandItemCode: 'EX26',
          },
          '4zd4onnmg2y5': {
            label: {
              en: 'Crystal White Tricoat with Black roof',
              ar: 'أبيض Crystal بثلاث طبقات مع سقف أسود',
            },
            image: 'images/2369/ext_colors/official/Crystal_White_Tricoat.jpg',
            usdz: 'blue_black',
            price: {
              KSA: 8039,
              UAE: 7889,
              OMR: 836,
              BHD: 815,
              QAR: 7803,
              JOD: 1522,
              KW: 665,
              LBP: 2144,
              IQD: 2144,
              default: 7889,
            },
            index: 21,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2369/ext_colors/official/Crystal_White_Tricoat.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'G1W',
            brandItemCode: 'EX66',
          },
          '4zd4onnmg2y5-2026-lyriq-v': {
            label: {
              en: 'Stellar Black Metallic',
              ar: 'أسود Stellar معدني',
            },
            image: 'images/2369/ext_colors/official/Stellar_Black_Metallic.jpg',
            usdz: 'blue_black',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              default: 0,
            },
            index: 22,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2369/ext_colors/official/Stellar_Black_Metallic.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'GB8',
            brandItemCode: 'EX28',
          },
          '2phvl5sq89cx': {
            label: {
              en: 'Radiant Red Tintcoat with black roof',
              ar: 'أحمر Radiant بطبقتين مع سقف أسود',
            },
            image: 'images/2369/ext_colors/official/Radiant_Red_Tintcoat.jpg',
            usdz: 'white_black',
            price: {
              KSA: 8039,
              UAE: 7889,
              OMR: 836,
              BHD: 815,
              QAR: 7803,
              JOD: 1522,
              KW: 665,
              LBP: 2144,
              IQD: 2144,
              default: 7889,
            },
            index: 23,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2369/ext_colors/official/Radiant_Red_Tintcoat.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'GNT',
            brandItemCode: 'EX86',
          },
          '2phvl5sq89cx-2026-lyriq-v': {
            label: {
              en: 'Opulent Blue Metallic',
              ar: 'أزرق Opulent معدني',
            },
            image: 'images/2369/ext_colors/official/Opulent_Blue_Metallic.jpg',
            usdz: 'white_black',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              default: 0,
            },
            index: 24,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2369/ext_colors/official/Opulent_Blue_Metallic.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'GTR',
            brandItemCode: 'EX84',
          },
        },
        interior: {
          '1c0z0wtq7ikc': {
            label: {
              en: 'Noir Inteluxe seating with Santorini Blue accents',
              ar: 'مقاعد Inteluxe باللون Noir مع لمسات باللون الأزرق Santorini',
            },
            image: 'images/2369/int_colors/official/Noir_Inteluxe_Santorini_Blue.jpg',
            usdz: 'interior_red',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              default: 0,
            },
            index: 0,
            type: 'interior',
            cardType: 'cart',
            cartImage: 'images/2369/int_colors/official/Noir_Inteluxe_Santorini_Blue.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'H7D',
            brandItemCode: 'IN64',
          },
          '0rl5mcxrsw1t': {
            label: {
              en: 'Oxford Stone Nappa leather seating with Garnet accents (Leather)',
              ar: 'مقاعد من جلد Nappa باللون Oxford Stone مع لمسات باللون Garnet',
            },
            image: 'images/2369/int_colors/official/Oxford_Stone_Nappa.jpg',
            usdz: 'interior_red',
            price: {
              KSA: 15932,
              UAE: 15635,
              OMR: 1657,
              BHD: 1614,
              QAR: 15465,
              JOD: 3017,
              KW: 1317,
              LBP: 4249,
              IQD: 4249,
              default: 15635,
            },
            index: 1,
            type: 'interior',
            cardType: 'cart',
            cartImage: 'images/2369/int_colors/official/Oxford_Stone_Nappa.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'H7A',
            brandItemCode: 'IN69',
          },
          hln6y3y86ug4: {
            label: {
              en: 'Juniper Nappa leather seating with Sky Cool Gray accents (Leather)',
              ar: 'مقاعد مكسوّة بجلد Nappa باللون Juniper مع لمسات باللون الرمادي Sky Cool',
            },
            image: 'images/2369/int_colors/official/Juniper_Nappa.jpg',
            usdz: 'interior_red',
            price: {
              KSA: 15932,
              UAE: 15635,
              OMR: 1657,
              BHD: 1614,
              QAR: 15465,
              JOD: 3017,
              KW: 1317,
              LBP: 4249,
              IQD: 4249,
              default: 15635,
            },
            index: 2,
            type: 'interior',
            cardType: 'cart',
            cartImage: 'images/2369/int_colors/official/Juniper_Nappa.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'H7B',
            brandItemCode: 'IN57',
          },
          yh3xn6m12i5m: {
            label: {
              en: 'Sky Cool Gray Inteluxe seating with Santorini Blue accents',
              ar: 'مقاعد Inteluxe باللون الرمادي Sky Cool مع لمسات باللون الأزرق Santorini',
            },
            image: 'images/2369/int_colors/official/Sky_Cool_Gray_Inteluxe_Santorini_Blue.jpg',
            usdz: 'interior_red',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              default: 0,
            },
            index: 3,
            type: 'interior',
            cardType: 'cart',
            cartImage: 'images/2369/int_colors/official/Sky_Cool_Gray_Inteluxe_Santorini_Blue.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'H7E',
            brandItemCode: 'IN79',
          },
          '48za413x82620': {
            label: {
              en: 'Noir Inteluxe seating with Santorini Blue accents',
              ar: 'مقاعد Inteluxe باللون Noir مع لمسات باللون الأزرق Santorini',
            },
            image: 'images/2369/int_colors/official/Noir_Inteluxe_Santorini_Blue.jpg',
            usdz: 'interior_red',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              default: 0,
            },
            index: 4,
            type: 'interior',
            cardType: 'cart',
            cartImage: 'images/2369/int_colors/official/Noir_Inteluxe_Santorini_Blue.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'H7D',
            brandItemCode: 'IN64',
          },
          '48za413x82620-2026-lyriq-v': {
            label: {
              en: 'Noir with Sky Cool Gray accents',
              ar: 'Noir  مع لمسات باللون الرمادي Sky Cool',
            },
            image: 'images/2369/int_colors/official/Noir_Sky_Cool_Gray.jpg',
            usdz: 'interior_red',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              default: 0,
            },
            index: 5,
            type: 'interior',
            cardType: 'cart',
            cartImage: 'images/2369/int_colors/official/Noir_Sky_Cool_Gray.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'ELK',
            brandItemCode: 'IN65',
          },
          '48za413x82621': {
            label: {
              en: 'Oxford Stone Nappa leather seating with Garnet accents (Leather)',
              ar: 'مقاعد من جلد Nappa باللون Oxford Stone مع لمسات باللون Garnet',
            },
            image: 'images/2369/int_colors/official/Oxford_Stone_Nappa.jpg',
            usdz: 'interior_red',
            price: {
              KSA: 15932,
              UAE: 15635,
              OMR: 1657,
              BHD: 1614,
              QAR: 15465,
              JOD: 3017,
              KW: 1317,
              LBP: 4249,
              IQD: 4249,
              default: 15635,
            },
            index: 6,
            type: 'interior',
            cardType: 'cart',
            cartImage: 'images/2369/int_colors/official/Oxford_Stone_Nappa.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'H7A',
            brandItemCode: 'IN69',
          },
          '48za413x82621-2026-lyriq-v': {
            label: {
              en: 'Oxford Stone Nappa leather seating with Garnet accents (Leather)',
              ar: 'مقاعد من جلد Nappa باللون Oxford Stone مع لمسات باللون Garnet',
            },
            image: 'images/2369/int_colors/official/Oxford_Stone_Nappa.jpg',
            usdz: 'interior_red',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              default: 0,
            },
            index: 7,
            type: 'interior',
            cardType: 'cart',
            cartImage: 'images/2369/int_colors/official/Oxford_Stone_Nappa.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'H7A',
            brandItemCode: 'IN69',
          },
          '48za413x82622': {
            label: {
              en: 'Juniper Nappa leather seating with Sky Cool Gray accents (Leather)',
              ar: 'مقاعد مكسوّة بجلد Nappa باللون Juniper مع لمسات باللون الرمادي Sky Cool',
            },
            image: 'images/2369/int_colors/official/Juniper_Nappa.jpg',
            usdz: 'interior_red',
            price: {
              KSA: 15932,
              UAE: 15635,
              OMR: 1657,
              BHD: 1614,
              QAR: 15465,
              JOD: 3017,
              KW: 1317,
              LBP: 4249,
              IQD: 4249,
              default: 15635,
            },
            index: 8,
            type: 'interior',
            cardType: 'cart',
            cartImage: 'images/2369/int_colors/official/Juniper_Nappa.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'H7B',
            brandItemCode: 'IN57',
          },
          '48za413x82623': {
            label: {
              en: 'Sky Cool Gray Inteluxe seating with Santorini Blue accents',
              ar: 'مقاعد Inteluxe باللون الرمادي Sky Cool مع لمسات باللون الأزرق Santorini',
            },
            image: 'images/2369/int_colors/official/Sky_Cool_Gray_Inteluxe_Santorini_Blue.jpg',
            usdz: 'interior_red',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              default: 0,
            },
            index: 9,
            type: 'interior',
            cardType: 'cart',
            cartImage: 'images/2369/int_colors/official/Sky_Cool_Gray_Inteluxe_Santorini_Blue.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'H7E',
            brandItemCode: 'IN79',
          },
        },
      },
      features: {
        '49vozj460qwe': {
          label: {
            en: 'Black Painted Roof',
            ar: 'سقف مطلي باللون الأسود',
          },
          image: 'images/2369/features/luxury/BlackPaintedRoof.png',
          title: {
            en: 'Black Painted Roof',
            ar: 'سقف مطلي باللون الأسود',
          },
          desc: {
            en: '',
            ar: '',
          },
          type: 'exterior',
        },
        '0gvs8s0ln4fh': {
          label: {
            en: 'Panaromic Fixed Sunroof',
            ar: 'سقف بانورامي زجاجي ثابت',
          },
          image: 'images/2369/features/luxury/BlackPaintedRoof.png',
          title: {
            en: 'Panaromic Fixed Sunroof',
            ar: 'سقف بانورامي زجاجي ثابت',
          },
          desc: {
            en: '',
            ar: '',
          },
          type: 'exterior',
        },
        nb8tkmndj3vg: {
          label: {
            en: 'Galvano exterior trim',
            ar: 'لمسات خارجية Galvano',
          },
          image: 'images/2369/features/luxury/Galvanoexteriortrim.png',
          title: {
            en: 'Galvano exterior trim',
            ar: 'لمسات خارجية Galvano',
          },
          desc: {
            en: '',
            ar: '',
          },
          type: 'exterior',
        },
        m37v8o8yl5x2: {
          label: {
            en: 'Alloy Wheels',
            ar: 'عجلات ألمنيوم',
          },
          image: 'images/2369/features/luxury/Standard21inchalloywheels.png',
          title: {
            en: 'Alloy Wheels',
            ar: 'عجلات ألمنيوم',
          },
          desc: {
            en: 'Standard 21-inch alloy wheels',
            ar: 'عجلات ألمنيوم قياس 21 بوصة',
          },
          type: 'exterior',
        },
        eo26k4ynyg3r: {
          label: {
            en: 'Illuminated door handles',
            ar: 'مقابض أبواب مضيئة',
          },
          image: 'images/2369/features/luxury/Illuminateddoorhandles.png',
          title: {
            en: 'Illuminated door handles',
            ar: 'مقابض أبواب مضيئة',
          },
          desc: {
            en: '',
            ar: '',
          },
          type: 'exterior',
        },
        xogfbqwjubb0: {
          label: {
            en: 'Automatic Climate Control',
            ar: 'نظام تكييف أوتوماتيكي',
          },
          image: 'images/2369/features/luxury/TrizoneAutomaticClimateControl.png',
          title: {
            en: 'Trizone- Automatic Climate Control',
            ar: 'نظام تكييف أوتوماتيكي لثلاث مناطق',
          },
          desc: {
            en: '',
            ar: '',
          },
          type: 'interior',
        },
        s4f98dj7g3mk: {
          label: {
            en: 'Seats',
            ar: 'مقاعد',
          },
          image: 'images/2369/features/luxury/RearHeatedSeats.png',
          title: {
            en: 'Rear Heated Seats',
            ar: 'مقاعد خلفية بخاصية التدفئة',
          },
          desc: {
            en: '',
            ar: '',
          },
          type: 'interior',
        },
        epg4m463go0z: {
          label: {
            en: 'LED Display',
            ar: 'شاشة LED',
          },
          image: 'images/2369/features/luxury/diagonaladvancedLEDdisplay.png',
          title: {
            en: 'LED Display',
            ar: 'شاشة LED',
          },
          desc: {
            en: '33" diagonal advanced LED display',
            ar: 'شاشة LED منحنية متطورة قياس 33 بوصة',
          },
          type: 'interior',
        },
        tvh5xpuhzsbz: {
          label: {
            en: 'Ambient lighting',
            ar: 'إضاءة محيطية',
          },
          image: 'images/2369/features/luxury/LuxuryAmbientLighting.png',
          title: {
            en: 'Ambient lighting',
            ar: 'إضاءة محيطية',
          },
          desc: {
            en: '126-color LED dual-zone ambient interior lighting',
            ar: 'إضاءة محيطية ثنائية المنطقة من 126 لوناً',
          },
          type: 'interior',
        },
        ngzrs5lwkgop: {
          label: {
            en: 'Seating Material',
            ar: 'خامة المقاعد',
          },
          image: 'images/2369/features/luxury/Inteluxeseatingwithperforatedinserts.png',
          title: {
            en: 'Seating Material',
            ar: 'خامة المقاعد',
          },
          desc: {
            en: 'Inteluxe seating with perforated inserts',
            ar: 'مقاعد بخامة Inteluxe مع أجزاء مفرّغة',
          },
          type: 'interior',
        },
        '2mn467pj8vy5': {
          label: {
            en: 'Front Seats',
            ar: 'مقاعد أمامية',
          },
          image: 'images/2369/features/luxury/HeatedFrontSeats.png',
          title: {
            en: 'Heater Front Seats',
            ar: 'مقاعد أمامية بخاصية التدفئة',
          },
          desc: {
            en: 'Heated front seats with 8-way power driver, 6-way power passenger adjustability with 4-way power lumbar, memory',
            ar: 'مقاعد أمامية بخاصية التدفئة مع إمكانية تعديل مقعد السائق كهربائياً في 8 اتجاهات ومقعد الراكب الأمامي في 6 اتجاهات، مع دعامة أسفل الظهر كهربائية في 4 اتجاهات، ونظام ذاكرة',
          },
          type: 'interior',
        },
        op4pfqvxb43s: {
          label: {
            en: 'Wireless phone charging',
            ar: 'شحن لاسلكي للهاتف',
          },
          image: 'images/2369/features/luxury/WirelessCharger.png',
          title: {
            en: 'Wireless phone charging',
            ar: 'شحن لاسلكي للهاتف',
          },
          desc: {
            en: '',
            ar: '',
          },
          type: 'interior',
        },
        '29kx4ja7eg7r': {
          label: {
            en: 'Black Painted Roof',
            ar: 'سقف مطلي باللون الأسود',
          },
          image: 'images/2369/features/sport/BlackPaintedRoof.png',
          title: {
            en: 'Black Painted Roof',
            ar: 'سقف مطلي باللون الأسود',
          },
          desc: {
            en: '',
            ar: '',
          },
          type: 'exterior',
        },
        '29kx4ja7eg7r-2026-lyriq-v': {
          label: {
            en: 'Black Painted Roof',
            ar: 'سقف مطلي باللون الأسود',
          },
          image: 'images/2369/lyriq_v_Roof.jpg',
          title: {
            en: 'Black Painted Roof',
            ar: 'سقف مطلي باللون الأسود',
          },
          desc: {
            en: '',
            ar: '',
          },
          type: 'exterior',
        },
        '3ud49gqncb2e': {
          label: {
            en: 'Panaromic Fixed Sunroof',
            ar: 'سقف بانورامي زجاجي ثابت',
          },
          image: 'images/2369/features/sport/BlackPaintedRoof.png',
          title: {
            en: 'Panaromic Fixed Sunroof',
            ar: 'سقف بانورامي زجاجي ثابت',
          },
          desc: {
            en: '',
            ar: '',
          },
          type: 'exterior',
        },
        '3ud49gqncb2e-2026-lyriq-v': {
          label: {
            en: 'Panaromic Fixed Sunroof',
            ar: 'سقف بانورامي زجاجي ثابت',
          },
          image: 'images/2369/lyriq_v_Sunroof.jpg',
          title: {
            en: 'Panaromic Fixed Sunroof',
            ar: 'سقف بانورامي زجاجي ثابت',
          },
          desc: {
            en: '',
            ar: '',
          },
          type: 'exterior',
        },
        aayf3bzuw1dj: {
          label: {
            en: 'Alloy Wheels',
            ar: 'عجلات ألمنيوم',
          },
          image: 'images/2369/features/sport/Standard21inchalloywheels.png',
          title: {
            en: 'Alloy Wheels',
            ar: 'عجلات ألمنيوم',
          },
          desc: {
            en: 'Standard 21-inch alloy wheels',
            ar: 'عجلات ألمنيوم قياس 21 بوصة',
          },
          type: 'exterior',
        },
        'aayf3bzuw1dj-2026-lyriq-v': {
          label: {
            en: 'Alloy Wheels',
            ar: 'عجلات ألمنيوم',
          },
          image: 'images/2369/lyriq_v_Wheels.jpg',
          title: {
            en: 'Alloy Wheels',
            ar: 'عجلات ألمنيوم',
          },
          desc: {
            en: 'Standard 21-inch alloy wheels',
            ar: 'عجلات ألمنيوم قياس 21 بوصة',
          },
          type: 'exterior',
        },
        '2g2uqgatmdw8': {
          label: {
            en: 'Illuminated door handles',
            ar: 'مقابض أبواب مضيئة',
          },
          image: 'images/2369/features/sport/Illuminateddoorhandles.png',
          title: {
            en: 'Illuminated door handles',
            ar: 'مقابض أبواب مضيئة',
          },
          desc: {
            en: '',
            ar: '',
          },
          type: 'exterior',
        },
        '2g2uqgatmdw8-2026-lyriq-v': {
          label: {
            en: 'Illuminated door handles',
            ar: 'مقابض أبواب مضيئة',
          },
          image: 'images/2369/lyriq_v_Handles.jpg',
          title: {
            en: 'Illuminated door handles',
            ar: 'مقابض أبواب مضيئة',
          },
          desc: {
            en: '',
            ar: '',
          },
          type: 'exterior',
        },
        '2ystj8x60810': {
          label: {
            en: 'Automatic Climate Control',
            ar: 'نظام تكييف أوتوماتيكي',
          },
          image: 'images/2369/features/sport/TrizoneAutomaticClimateControl.png',
          title: {
            en: 'Trizone- Automatic Climate Control',
            ar: 'نظام تكييف أوتوماتيكي لثلاث مناطق',
          },
          desc: {
            en: '',
            ar: '',
          },
          type: 'interior',
        },
        '2ystj8x60810-2026-lyriq-v': {
          label: {
            en: 'Automatic Climate Control',
            ar: 'نظام تكييف أوتوماتيكي',
          },
          image: 'images/2369/lyriq_v_AC_Control.jpg',
          title: {
            en: 'Trizone- Automatic Climate Control',
            ar: 'نظام تكييف أوتوماتيكي لثلاث مناطق',
          },
          desc: {
            en: '',
            ar: '',
          },
          type: 'interior',
        },
        or7mlsx2z2tp: {
          label: {
            en: 'Seats',
            ar: 'مقاعد',
          },
          image: 'images/2369/features/sport/RearHeatedSeats.png',
          title: {
            en: 'Rear Heated Seats',
            ar: 'مقاعد خلفية بخاصية التدفئة',
          },
          desc: {
            en: '',
            ar: '',
          },
          type: 'interior',
        },
        'or7mlsx2z2tp-2026-lyriq-v': {
          label: {
            en: 'Seats',
            ar: 'مقاعد',
          },
          image: 'images/2369/lyriq_v_Rear_Heated_Seats.jpg',
          title: {
            en: 'Rear Heated Seats',
            ar: 'مقاعد خلفية بخاصية التدفئة',
          },
          desc: {
            en: '',
            ar: '',
          },
          type: 'interior',
        },
        tppihp9c8m7o: {
          label: {
            en: 'LED Display',
            ar: 'شاشة LED',
          },
          image: 'images/2369/features/sport/LedDisplay.png',
          title: {
            en: 'LED Display',
            ar: 'شاشة LED',
          },
          desc: {
            en: '33" diagonal advanced LED display',
            ar: 'شاشة LED منحنية متطورة قياس 33 بوصة',
          },
          type: 'interior',
        },
        'tppihp9c8m7o-2026-lyriq-v': {
          label: {
            en: 'LED Display',
            ar: 'شاشة LED',
          },
          image: 'images/2369/lyriq_v_Info_Screen.jpg',
          title: {
            en: 'LED Display',
            ar: 'شاشة LED',
          },
          desc: {
            en: '33" diagonal advanced LED display',
            ar: 'شاشة LED منحنية متطورة قياس 33 بوصة',
          },
          type: 'interior',
        },
        gaumznh1p962: {
          label: {
            en: 'Ambient lighting',
            ar: 'إضاءة محيطية',
          },
          image: 'images/2369/features/sport/SportAmbientLighting.png',
          title: {
            en: 'Ambient lighting',
            ar: 'إضاءة محيطية',
          },
          desc: {
            en: '126-color LED dual-zone ambient interior lighting',
            ar: 'إضاءة محيطية ثنائية المنطقة من 126 لوناً',
          },
          type: 'interior',
        },
        'gaumznh1p962-2026-lyriq-v': {
          label: {
            en: 'Ambient lighting',
            ar: 'إضاءة محيطية',
          },
          image: 'images/2369/lyriq_v_Ambient_Lighting.jpg',
          title: {
            en: 'Ambient lighting',
            ar: 'إضاءة محيطية',
          },
          desc: {
            en: '126-color LED dual-zone ambient interior lighting',
            ar: 'إضاءة محيطية ثنائية المنطقة من 126 لوناً',
          },
          type: 'interior',
        },
        '1jhh1m7485kl': {
          label: {
            en: 'Wireless phone charging',
            ar: 'شحن لاسلكي للهاتف',
          },
          image: 'images/2369/features/sport/Sport_WirelessCharger.png',
          title: {
            en: 'Wireless phone charging',
            ar: 'شحن لاسلكي للهاتف',
          },
          desc: {
            en: '',
            ar: '',
          },
          type: 'interior',
        },
        '1jhh1m7485kl-2026-lyriq-v': {
          label: {
            en: 'Wireless phone charging',
            ar: 'شحن لاسلكي للهاتف',
          },
          image: 'images/2369/lyriq_v_Wireless_Charger.jpg',
          title: {
            en: 'Wireless phone charging',
            ar: 'شحن لاسلكي للهاتف',
          },
          desc: {
            en: '',
            ar: '',
          },
          type: 'interior',
        },
        '2g2uffqgatmdw8': {
          label: {
            en: 'Dark Accent Side Trim',
            ar: 'حواف جانبية داكنة اللون',
          },
          image: 'images/2369/lyriq_v_Side_Trim.jpg',
          title: {
            en: 'Dark Accent Side Trim',
            ar: 'حواف جانبية داكنة اللون',
          },
          desc: {
            en: '',
            ar: '',
          },
          type: 'exterior',
        },
        '2g09ffqgatmdw8': {
          label: {
            en: 'Continuous Damping Control',
            ar: 'نظام التعليق التكيفي المستمر',
          },
          image: 'images/2369/lyriq_v_Suspension.jpg',
          title: {
            en: 'Continuous Damping Control',
            ar: 'نظام التعليق التكيفي المستمر',
          },
          desc: {
            en: 'Advanced adaptive valve-based damper suspension technology provides the LYRIQ-V with Cadillac ride isolation and V-Series driving precision',
            ar: 'توفر تقنية التعليق المتقدمة القائمة على الصمامات التكيفية لسيارة LYRIQ-V عزل كاديلاك الممتاز للقيادة ودقة أداء سلسلة V',
          },
          type: 'exterior',
        },
        '2g0mjiopqgatmdw8': {
          label: {
            en: 'Brembo Performance Front Brakes',
            ar: 'مكابح Brembo الأمامية عالية الأداء',
          },
          image: 'images/2369/lyriq_v_Brakes.jpg',
          title: {
            en: 'Brembo Performance Front Brakes',
            ar: 'مكابح Brembo الأمامية عالية الأداء',
          },
          desc: {
            en: 'Featuring dynamic six-piston fixed calipers and available low metallic linings to help deliver sure-footed stopping power',
            ar: 'تتميز بملاقط مكابح ثابتة ديناميكية بستة مكابس، وبطانات خفيفة المعادن لتوفير قوة كبح ثابتة وواثقة',
          },
          type: 'exterior',
        },
        '1jhh1mvyo7485kl': {
          label: {
            en: 'Velocity Max',
            ar: 'وضع القيادة Velocity Max',
          },
          image: 'images/2369/lyriq_v_Velocity_Max.jpg',
          title: {
            en: 'Velocity Max',
            ar: 'وضع القيادة Velocity Max',
          },
          desc: {
            en: 'When activated, Velocity Max enables a Cadillac-estimated 615 horsepower and 881 Nm of torque and 0-96 km/h in 3.3 seconds with Launch Control',
            ar: 'عند تفعيل وضع Velocity Max، توفر كاديلاك قوةً تقديرية تبلغ 615 حصاناً وعزم دوران يبلغ 881 نيوتن متر، مع تسارع من 0 إلى 96 كم/س خلال 3.3 ثانية باستخدام نظام Launch Control.',
          },
          type: 'interior',
        },
        '1jhh1mvfffyo7485kl': {
          label: {
            en: 'Seating Material',
            ar: 'خامة المقاعد',
          },
          image: 'images/2369/lyriq_v_Front_Seats.jpg',
          title: {
            en: 'Seating Material',
            ar: 'خامة المقاعد',
          },
          desc: {
            en: 'Inteluxe seating with perforated inserts',
            ar: 'مقاعد بخامة Inteluxe مع أجزاء مفرّغة',
          },
          type: 'interior',
        },
      },
      accessories: {
        '3ckvx6gvve78': {
          label: {
            en: 'SPORT_VARIANTS_PARTS',
            ar: 'مكوّنات فئة SPORT',
          },
          image: '',
          category: {
            en: 'Roof',
            ar: 'السقف',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
        },
        '3ckvx6gvve78-2026-lyriq-v': {
          label: {
            en: 'Vseries_Variant_Parts',
            ar: 'مكوّنات فئة V-Series',
          },
          image: '',
          category: {
            en: 'Roof',
            ar: 'السقف',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
        },
        '3ckvcvcx6gvve78': {
          label: {
            en: 'Vseries_Caliper_Default',
            ar: 'ملاقط مكابح V-Series القياسية',
          },
          image: '',
          category: {
            en: 'Brake Calipers',
            ar: 'ملاقط الفرامل',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
        },
        '3ckkvvcvcx6gvve78': {
          label: {
            en: 'Calipers, Red painted, with V-Series logo on front',
            ar: 'ملاقط مكابح مطلية باللون الأحمر، مع شعار V-Series في الأمام',
          },
          image: 'images/2369/lyriq_v_Optional_Brake.jpg',
          category: {
            en: 'Brake Calipers',
            ar: 'ملاقط الفرامل',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
          autoIntelRPOCode: 'J6N',
          brandItemCode: 'AC161',
        },
      },
      packages: {},
      // Brake calipers render as their own Exterior section, matching CT5 (2384).
      // `options: []` keeps the Options tab empty rather than falling back to the
      // generic 'Accessories' group — Lyriq has no option-tab accessories.
      accessoryCategories: {
        exterior: ['Brake Calipers'],
        interior: [],
        options: [],
      },
      interactions: {
        f6mckp6y4528: {
          label: {
            en: 'Doors',
            ar: 'الأبواب',
          },
          type: 'interaction',
        },
        tbur04pwvnag: {
          label: {
            en: 'Boot',
            ar: 'صندوق المركبة',
          },
          type: 'interaction',
        },
        iprifpuhmp7l: {
          label: {
            en: 'Headlight',
            ar: 'ضوء السقف',
          },
          type: 'interaction',
        },
        vbjs341ccuuu: {
          label: {
            en: 'DRL',
            ar: 'DRL',
          },
          type: 'interaction',
        },
        gvx72nh2lk38: {
          label: {
            en: 'Taillight',
            ar: 'الضوء الخلفي',
          },
          type: 'interaction',
        },
        haleh6q9o6o2: {
          label: {
            en: 'Sunroof Shade',
            ar: 'ستارة السقف',
          },
          type: 'interaction',
        },
        f9s7b4hdeiz7: {
          label: {
            en: 'Doors',
            ar: 'الأبواب',
          },
          type: 'interaction',
        },
        '1nad76ictp6j': {
          label: {
            en: 'Boot',
            ar: 'صندوق المركبة',
          },
          type: 'interaction',
        },
        '3cqy8q29b96c': {
          label: {
            en: 'Headlight',
            ar: 'ضوء السقف',
          },
          type: 'interaction',
        },
        '2ca59s6yd3u9': {
          label: {
            en: 'DRL',
            ar: 'DRL',
          },
          type: 'interaction',
        },
        '1dzdmvhl8vp3': {
          label: {
            en: 'Taillight',
            ar: 'الضوء الخلفي',
          },
          type: 'interaction',
        },
        mAlX4SRB_per: {
          label: {
            en: 'Sunroof Shade',
            ar: 'ستارة السقف',
          },
          type: 'interaction',
        },
        'mAlX4SRB_per-2026-lyriq-v': {
          label: {
            en: 'Sunroof',
            ar: 'ستارة',
          },
          type: 'interaction',
        },
      },
      hotspots: {
        exteriorCamera: {
          title: {
            en: 'Exterior',
            ar: 'الخارج',
          },
        },
        interiorCamera: {
          title: {
            en: 'First Row Seat',
            ar: 'مقعد الصف الأول',
          },
        },
        interiorCamera_1: {
          title: {
            en: 'Second Row Seat',
            ar: 'مقعد الصف الثاني',
          },
        },
      },
      name: {
        en: 'Cadillac Lyriq',
        ar: 'كاديلاك ليريك',
      },
      specifications: {
        heading: {
          en: 'Cadillac Lyriq',
          ar: 'كاديلاك ليريك',
        },
        trim: {
          en: 'LYRIQ-V',
          ar: 'LYRIQ-V',
        },
        price: {
          en: 'Starting from 330000 MSRP',
          ar: 'ابتداء من 330000 MSRP',
        },
        model: 'Cadillac Lyriq',
        seatingCapacity: '5 Seats',
        dimensions: {
          length: '5001 mm',
          width: '',
          height: '',
          wheelbase: '3094 mm',
          groundClearance: '',
        },
        performance: [
          {
            value: '515 HP',
            unit: '',
            label: {
              en: 'Horsepower',
              ar: 'حصان',
            },
          },
          {
            value: '610 NM',
            unit: '',
            label: {
              en: 'Torque',
              ar: 'عزم الدوران',
            },
          },
          {
            value: '463 km',
            unit: '',
            label: {
              en: 'Range',
              ar: 'المدى',
            },
          },
        ],
        summary: [
          {
            title: {
              en: 'Model',
              ar: 'الطراز',
            },
            subtitle: {
              en: 'CADILLAC LYRIQ-V',
              ar: 'كاديلاك LYRIQ-V',
            },
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              default: 0,
            },
          },
          {
            title: {
              en: 'Exterior Colors',
              ar: 'الألوان الخارجية',
            },
            subtitle: {
              en: 'Argent Silver Metallic, Stellar Black Metallic, Radiant Red Tintcoat, Vibrant White Tricoat, Emerald Lake Metallic, Nimbus Metallic, Opulent Blue Metallic',
              ar: 'فضي Argent معدني، أسود Stellar معدني، أحمر Radiant بطبقتين، أبيض Vibrant بثلاث طبقات، أزرق Emerald Lake معدني، Nimbus معدني، أزرق Opulent معدني',
            },
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              default: 0,
            },
          },
          {
            title: {
              en: 'Interior',
              ar: 'التصميم الداخلي',
            },
            subtitle: {
              en: 'Noir with Sky Cool Gray accents, Oxford Stone with Garnet accents',
              ar: 'Noir  مع لمسات باللون الرمادي Sky Cool، وOxford Stone مع لمسات Garnet',
            },
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              default: 0,
            },
          },
          {
            title: {
              en: 'Infotainment Screen',
              ar: 'شاشة المعلومات والترفيه',
            },
            subtitle: {
              en: 'Segment-largest 33” diagonal advanced LED display with 9K resolution',
              ar: 'أكبر شاشة LED متطورة في فئتها بقياس قطري 33 بوصة وبدقة 9K',
            },
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              default: 0,
            },
          },
          {
            title: {
              en: 'Wheelbase',
              ar: 'قاعدة العجلات',
            },
            subtitle: {
              en: '3094 mm',
              ar: '3094 مم',
            },
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              default: 0,
            },
          },
          {
            title: {
              en: 'Total Length',
              ar: 'الطول الإجمالي',
            },
            subtitle: {
              en: '5001 mm',
              ar: '5001 مم',
            },
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              default: 0,
            },
          },
          {
            title: {
              en: 'Cargo Volume (Behind 1st Row Seat)',
              ar: 'سعة الأمتعة خلف مقاعد الصف الأول',
            },
            subtitle: {
              en: '1721.9 litres',
              ar: '1721.9 لتر',
            },
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              default: 0,
            },
          },
          {
            title: {
              en: 'Cargo Volume (Behind 2nd Row Seat)',
              ar: 'سعة الأمتعة خلف مقاعد الصف الثاني',
            },
            subtitle: {
              en: '793 litres',
              ar: '793 لتر',
            },
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              default: 0,
            },
          },
          {
            title: {
              en: 'Powertrain',
              ar: 'نظام الدفع',
            },
            subtitle: {
              en: 'All Wheel Drive',
              ar: 'دفع رباعي',
            },
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              default: 0,
            },
          },
          {
            title: {
              en: 'OnStar Basics',
              ar: 'باقة OnStar الأساسية',
            },
            subtitle: {
              en: '8 Years',
              ar: '8 سنوات',
            },
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              default: 0,
            },
          },
          {
            title: {
              en: 'OnStar One',
              ar: 'باقة OnStar One',
            },
            subtitle: {
              en: 'Upto 3 Years',
              ar: 'حتى 3 سنوات',
            },
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              default: 0,
            },
          },
          {
            title: {
              en: 'Seating Capacity',
              ar: 'سعة المقاعد',
            },
            subtitle: {
              en: '5 Seats',
              ar: '5 مقاعد',
            },
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              default: 0,
            },
          },
        ],
      },
    },
  },
  plugins: {
    deferredAssetLoading: true,
  },
  staticTranslations: {
    specDetails: {
      model: {
        en: 'Model',
        ar: 'نموذج',
      },
      exteriorColours: {
        en: 'Exterior',
        ar: 'الخارجة',
      },
      interiorAmbience: {
        en: 'Interior',
        ar: 'الداخلية',
      },
      seatingCapacity: {
        en: 'Seating Capacity',
        ar: 'سعة المقاعد',
      },
      length: {
        en: 'Length',
        ar: 'الطول',
      },
      width: {
        en: 'Width',
        ar: 'العرض',
      },
      height: {
        en: 'Height',
        ar: 'الارتفاع',
      },
      wheelbase: {
        en: 'Wheelbase',
        ar: 'قاعدة العجلات',
      },
      groundClearance: {
        en: 'Ground Clearance',
        ar: 'الخلوص الأرضي',
      },
      share: {
        en: 'Share',
        ar: 'يشارك',
      },
    },
    variantSelection: {
      title: {
        en: 'Model',
        ar: 'نموذج',
      },
      select: {
        en: 'Select',
        ar: 'تحديد',
      },
      cancel: {
        en: 'Cancel',
        ar: 'إلغاء',
      },
      selectBodyStyle: {
        en: 'Select Body Style',
        ar: 'اختر نمط الهيكل',
      },
      availableTrims: {
        en: 'Select Trim',
        ar: 'اختر الفئة',
      },
      driveType: {
        en: 'Drive Type',
        ar: 'نوع القيادة',
      },
      model: {
        en: 'Model',
        ar: 'الموديل',
      },
    },
    loadingScreen: {
      default: {
        en: 'Preparing the canvas for your dream car. Just a few more seconds to go!',
        ar: 'لحظات ونبدأ رحلة تصميم سيارة أحلامك... باقي ثوانٍ قليلة!',
      },
      enterConfigurator: {
        en: 'Build Your Ride',
        ar: 'ابنِ سيارتك',
      },
      loading: {
        en: 'Loading',
        ar: 'جارٍ التحميل',
      },
      variantLoading: {
        en: 'Trim is loading',
        ar: 'نموذج جارٍ التحميل',
      },
      viewLoading: {
        en: 'Applying final touches',
        ar: 'تطبيق اللمسات النهائية',
      },
      disclaimer: {
        en: 'Disclaimer',
        ar: 'تنصل',
      },
      termsConditions: {
        en: 'Terms & Conditions',
        ar: 'الشروط والأحكام',
      },
      viewSpecDetails: {
        en: 'View Spec Details',
        ar: 'ععرض تفاصيل المواصفات',
      },
      disclaimerContent: {
        en: 'Prices are subject to change without prior notice and may vary based on location, availability, or other factors.',
        ar: 'الأسعار عرضة للتغيير دون إشعار مسبق وقد تختلف بناءً على الموقع والتوافر وعوامل أخرى.',
      },
      termsModal: {
        title: {
          en: 'Configurator Use Terms and Conditions',
          ar: 'شروط وأحكام استخدام المُكوِّن',
        },
        lead: {
          en: 'By using this vehicle configurator and submitting your build, you acknowledge and agree to the following:',
          ar: 'من خلال استخدام مُكوِّن تكوين السيارة هذا وإرسال تصميمك، فإنك تقر وتوافق على ما يلي:',
        },
        sections: {
          item1: {
            title: {
              en: 'Non-Binding Submission',
              ar: 'تقديم غير ملزم',
            },
            content: {
              en: 'Submitting your vehicle configuration does not constitute a formal vehicle order or purchase agreement. No payment or deposit is collected at this stage.',
              ar: 'إرسال مواصفات سيارتك لا يُعد طلبًا رسميًا للسيارة أو اتفاقية شراء. لا يتم تحصيل أي مبلغ أو دفعة مقدمة في هذه المرحلة.',
            },
          },
          item2: {
            title: {
              en: 'Preliminary Estimate',
              ar: 'تقدير أولي',
            },
            content: {
              en: 'All pricing displayed is Manufacturer’s Suggested Retail Price (MSRP)and is provided for informational purposes only. Final pricing, taxes, fees, and applicable offers will be confirmed by the authorized dealer.',
              ar: 'جميع الأسعار المعروضة هي سعر التجزئة المقترح من الشركة المصنعة، وهي لأغراض إعلامية فقط. سيتم تأكيد الأسعار النهائية والضرائب والرسوم والعروض المطبقة من قبل الوكيل المعتمد.',
            },
          },
          item3: {
            title: {
              en: 'Dealer Follow-Up',
              ar: 'متابعة التاجر',
            },
            content: {
              en: 'Your submitted configuration will be shared with a participating dealer, who will contact you to discuss your preferences, availability, and next steps.',
              ar: 'سيتم مشاركة التكوين الذي أرسلته مع أحد التجار المشاركين، والذي سيتصل بك لمناقشة تفضيلاتك وتوافرك والخطوات التالية.',
            },
          },
          item4: {
            title: {
              en: 'Availability and Fulfillment',
              ar: 'التوفر والوفاء',
            },
            content: {
              en: 'We will make every reasonable effort to fulfill your preferences; however, final vehicle and accessories selection is subject to dealer inventory and production availability. Configurations may be limited to in-stock or incoming vehicles.',
              ar: 'سنبذل قصارى جهدنا لتلبية تفضيلاتكم، إلا أن الاختيار النهائي للمركبات والملحقات يخضع لمخزون الوكيل وتوافر الإنتاج. قد تقتصر التكوينات على المركبات المتوفرة في المخزون أو الواردة.',
            },
          },
          item5: {
            title: {
              en: 'No Obligation',
              ar: 'لا يوجد التزام',
            },
            content: {
              en: 'You are under no obligation to purchase the configured vehicle, and submitting your configuration does not create a contractual relationship between you and the manufacturer or dealer.',
              ar: 'لا يوجد أي التزام عليك بشراء السيارة المخصصة، كما أن إرسال التكوين الخاص بك لا يؤدي إلى إنشاء علاقة تعاقدية بينك وبين الشركة المصنعة أو التاجر.',
            },
          },
          item6: {
            title: {
              en: 'Use of Personal Information and Consent',
              ar: 'استخدام المعلومات الشخصية والموافقة',
            },
            content: {
              en: 'By submitting your configuration, you consent to the collection, use, and sharing of your personal information with authorized dealers to facilitate your request, in accordance with our [Privacy Statement].',
              ar: 'من خلال إرسال التكوين الخاص بك، فإنك توافق على جمع معلوماتك الشخصية واستخدامها ومشاركتها مع التجار المعتمدين لتسهيل طلبك، وفقًا لـ [بيان الخصوصية] الخاص بنا.',
            },
          },
          item7: {
            title: {
              en: 'System Limitations',
              ar: 'قيود النظام',
            },
            content: {
              en: 'While we aim to provide accurate and current information, the configurator may not reflect all available features or up-to-date pricing. Errors or omissions may occur.',
              ar: 'مع أننا نهدف إلى توفير معلومات دقيقة وحديثة، إلا أن مُكوِّن الإعدادات قد لا يعكس جميع الميزات المتاحة أو الأسعار المُحدَّثة. قد تحدث أخطاء أو سهو.',
            },
          },
        },
      },
    },
    common: {
      exit: {
        en: 'Exit',
        ar: 'خروج',
      },
      close: {
        en: 'Close',
        ar: 'إغلاق',
      },
      cancel: {
        en: 'Cancel',
        ar: 'إلغاء',
      },
      back: {
        en: 'Back',
        ar: 'خلف',
      },
      next: {
        en: 'Next',
        ar: 'التالي',
      },
      done: {
        en: 'Done',
        ar: 'تم',
      },
      exterior: {
        en: 'Exterior',
        ar: 'الخارجة',
      },
      interior: {
        en: 'Interior',
        ar: 'الداخلية',
      },
      enterFullscreen: {
        en: 'Enter Fullscreen',
        ar: 'ملء الشاشة',
      },
      exitFullscreen: {
        en: 'Exit Fullscreen',
        ar: 'إلغاء ملء الشاشة',
      },
      selectTrim: {
        en: 'Select Trim',
        ar: 'اختر الفئة',
      },
      viewConfiguration: {
        en: 'View Configuration',
        ar: 'عرض التكوين',
      },
      togglePanelTooltip: {
        en: 'Select Trim and Configuration',
        ar: 'اختر الفئة والتكوين',
      },
      nextStepTooltip: {
        en: 'Click to proceed to the next step',
        ar: 'انقر للانتقال إلى الخطوة التالية',
      },
      heading: {
        en: 'Standard Features',
        ar: 'المميزات  القياسية',
      },
      description: {
        en: 'Chevrolet Arabia',
        ar: 'شيفروليه العربية',
      },
      goBack: {
        en: 'Go Back',
        ar: 'العودة',
      },
      freeRoamView: {
        en: 'Free Roam View',
        ar: 'عرض التجوال الحر',
      },
      featureView: {
        en: 'Featured View',
        ar: 'عرض مميز',
      },
      configurator: {
        en: 'Configurator',
        ar: 'المُكوِّن',
      },
      buildYourRide: {
        en: 'Build Your Ride',
        ar: 'ابنِ سيارتك',
      },
      options: {
        en: 'Options',
        ar: 'الخيارات',
      },
      summary: {
        en: 'Summary',
        ar: 'الملخص',
      },
      saveYourBuild: {
        en: 'Save Your Build',
        ar: 'احفظ تكوينك',
      },
      startingPrice: {
        en: 'Starting Price',
        ar: 'السعر الابتدائي',
      },
      msrp: {
        en: 'MSRP',
        ar: 'سعر التجزئة المقترح',
      },
      proceed: {
        en: 'Proceed',
        ar: 'متابعة',
      },
    },
    tabs: {
      config: {
        en: 'Config',
        ar: 'الإعداد',
      },
      model: {
        en: 'Model',
        ar: 'الموديل',
      },
      exterior: {
        en: 'Exterior',
        ar: 'الخارجي',
      },
      interior: {
        en: 'Interior',
        ar: 'الداخلي',
      },
      options: {
        en: 'Options',
        ar: 'الخيارات',
      },
      summary: {
        en: 'Summary',
        ar: 'الملخص',
      },
    },
    cart: {
      title: {
        en: 'Your Order',
        ar: 'طلبك',
      },
      disclaimer: {
        en: 'Accessory prices and availability will be confirmed at the showroom.',
        ar: 'سيتم تأكيد أسعار الملحقات وتوافرها في صالة العرض.',
      },
      exteriorLabel: {
        en: 'Exterior:',
        ar: ':خارجي',
      },
      interiorLabel: {
        en: 'Interior:',
        ar: ':داخلي',
      },
      addedAccessories: {
        en: 'Added Accessories',
        ar: 'الإكسسوارات المضافة',
      },
      priceEstimate: {
        en: 'Price Estimate',
        ar: 'السعر التقديري',
      },
      priceBreakdown: {
        en: 'Price Breakdown',
        ar: 'تفاصيل السعر',
      },
      reserveNow: {
        en: 'Proceed',
        ar: 'متابعة',
      },
      removeAccessory: {
        en: 'Remove accessory',
        ar: 'إزالة الإكسسوار',
      },
      removeItem: {
        en: 'Remove',
        ar: 'إزالة',
      },
      packageCategory: {
        en: 'Package',
        ar: 'حزمة',
      },
    },
    saveDialog: {
      optionsTitle: {
        en: 'How would you like to proceed?',
        ar: 'كيف تريد المتابعة؟',
      },
      optionWhatsapp: {
        en: 'Save to WhatsApp',
        ar: 'حفظ على واتساب',
      },
      optionEmail: {
        en: 'Save to Email',
        ar: 'حفظ على البريد الإلكتروني',
      },
      optionTestDrive: {
        en: 'Book a Test Drive',
        ar: 'احجز اختبار قيادة',
      },
      optionCallback: {
        en: 'Request a call Back',
        ar: 'طلب معاودة الاتصال',
      },
      labelFirstName: {
        en: 'First Name*',
        ar: 'الاسم الأول',
      },
      labelLastName: {
        en: 'Last Name*',
        ar: 'اسم العائلة',
      },
      labelEmail: {
        en: 'Email*',
        ar: 'البريد الالكتروني',
      },
      labelMobileNumber: {
        en: 'Mobile Number*',
        ar: 'رقم الجوال',
      },
      labelPrefix: {
        en: 'Prefix',
        ar: 'البادئة',
      },
      labelModel: {
        en: 'Model',
        ar: 'نموذج',
      },
      labelDealership: {
        en: 'Preferred Dealership*',
        ar: 'الوكيل المفضّل',
      },
      labelComments: {
        en: 'Comments',
        ar: 'تعليقات',
      },
      labelTestDriveDate: {
        en: 'Preferred date*',
        ar: 'التاريخ المفضل*',
      },
      labelTestDriveTime: {
        en: 'Preferred time*',
        ar: 'الوقت المفضل*',
      },
      placeholderTestDriveDate: {
        en: 'Preferred date*',
        ar: 'التاريخ المفضل*',
      },
      placeholderTestDriveTime: {
        en: 'Preferred time*',
        ar: 'الوقت المفضل*',
      },
      placeholderFirstName: {
        en: 'First Name*',
        ar: 'الاسم الأول',
      },
      placeholderLastName: {
        en: 'Last Name*',
        ar: 'اسم العائلة',
      },
      placeholderEmail: {
        en: 'Email*',
        ar: 'البريد الالكتروني',
      },
      placeholderMobileNumber: {
        en: 'Mobile Number*',
        ar: 'رقم الجوال',
      },
      placeholderComments: {
        en: 'Comments',
        ar: 'تعليقات',
      },
      placeholderSelectDealership: {
        en: 'Preferred Dealership*',
        ar: 'الوكيل المفضّل',
      },
      privacyTitle: {
        en: 'Privacy statement & legal disclaimer',
        ar: 'بيان الخصوصية وإخلاء المسؤولية القانونية',
      },
      privacyText: {
        en: 'I acknowledge and understand that my information will be shared with General Motors, its affiliates, dealer network or other parties are required by law for compliance, safety campaigns, government inquiries, or similar legal process. My information may also be shared for product research and development purposes, and to manage customer relationships to provide support. GM’s Privacy Statement addresses how GM handles the personal information shared with us.',
        ar: 'أقر وأتفهم بأنة سيتم مشاركة معلوماتي مع شركة جنرال موتورز، والشركات التابعة لها، وشبكة الوكلاء أو الأطراف الأخرى بموجب القانون للامتثال وحملات السلامة واستفسارات الحكومية وأي إجراء قانوني مماثل. وقد تتم مشاركة معلوماتي أيضًا لأغراض البحث وتطوير المنتجات ، وإدارة علاقة العملاء لتوفير الدعم اللازم. يوضح بيان خصوصية جنرال موتورز على موقعنا كيفية تعامل جنرال موتورز مع المعلومات الشخصية التي تمت مشاركتها معنا.',
      },
      privacyReadMore: {
        en: 'Read more',
        ar: 'اقرأ المزيد',
      },
      privacyReadMoreUrl: {
        en: 'https://www.cadillacarabia.com/ae-en/privacy-statement',
        ar: 'https://www.cadillacarabia.com/ae-ar/privacy-statement',
      },
      privacyMarketingConsent: {
        en: 'I wish to receive any marketing information or have my information shared with third parties for purposes of providing me with marketing information.',
        ar: 'أرغب في تلقي معلومات تسويقية أو مشاركة معلوماتي مع جهات خارجية لغاية تزويدي بمعلومات تسويقية',
      },
      buttonSend: {
        en: 'Send Configuration',
        ar: 'إرسال التكوين',
      },
      buttonSending: {
        en: 'Submitting...',
        ar: 'تقديم...',
      },
      buttonSent: {
        en: 'Sent!',
        ar: 'مرسل!',
      },
      buttonRetry: {
        en: 'Retry',
        ar: 'إعادة المحاولة',
      },
      buttonClose: {
        en: 'Okay, Got it',
        ar: 'حسنًا، فهمت',
      },
      successTitle: {
        en: 'Saved',
        ar: 'محفوظ',
      },
      successLabelModel: {
        en: 'Model Name',
        ar: 'اسم النموذج',
      },
      successLabelVariant: {
        en: 'Variant Name',
        ar: 'اسم البديل',
      },
      successLabelName: {
        en: 'Name',
        ar: 'اسم',
      },
      successLabelEmail: {
        en: 'Email*',
        ar: 'البريد الالكتروني',
      },
      successLabelMobile: {
        en: 'Mobile Number*',
        ar: 'رقم الجوال',
      },
      qrTitle: {
        en: 'Your Unique Configuration QR',
        ar: 'رمز الاستجابة السريعة لتكوينك',
      },
      qrDescription: {
        en: 'Show this QR code to your nearest dealer to access your saved configuration.',
        ar: 'أظهر رمز الاستجابة السريعة هذا لتاجر التجزئة الأقرب للوصول إلى التكوين المحفوظ.',
      },
      qrNoteLabel: {
        en: 'Note:',
        ar: 'ملاحظة:',
      },
      qrNote: {
        en: 'Only authorized dealers can view this configuration.',
        ar: 'يمكن للوكلاء المعتمدين فقط عرض هذا التكوين.',
      },
      qrDownload: {
        en: 'Download QR',
        ar: 'تحميل QR',
      },
      qrSave: {
        en: 'Save QR',
        ar: 'حفظ QR',
      },
      errorTitle: {
        en: 'Submission Failed',
        ar: 'فشل التقديم',
      },
      errorMessage: {
        en: 'We encountered an error, please try again.',
        ar: 'واجهنا خطأ، يرجى المحاولة مرة أخرى.',
      },
    },
    featureGroups: {
      exteriorDesign: {
        en: 'Exterior Design & Styling',
        ar: 'التصميم الخارجي والتصميم',
      },
      interiorComfort: {
        en: 'Interior Comfort & Features',
        ar: 'الراحة والميزات الداخلية',
      },
      technologyConvenience: {
        en: 'Technology & Convenience',
        ar: 'التكنولوجيا والراحة',
      },
      safetyAssistance: {
        en: 'Safety & Driver Assistance',
        ar: 'السلامة ومساعدة السائق',
      },
    },
    sidePanel: {
      menu: {
        en: 'Main Menu',
        ar: 'القائمة الرئيسية',
      },
      search: {
        en: 'Search',
        ar: 'بحث',
      },
      settings: {
        en: 'Settings',
        ar: 'الإعدادات',
      },
      profile: {
        en: 'Profile',
        ar: 'الملف الشخصي',
      },
      notifications: {
        en: 'Notifications',
        ar: 'الإشعارات',
      },
      help: {
        en: 'Help & Support',
        ar: 'المساعدة والدعم',
      },
      filter: {
        en: 'Filter Options',
        ar: 'خيارات التصفية',
      },
      favorites: {
        en: 'Favorites',
        ar: 'المفضلة',
      },
      history: {
        en: 'History',
        ar: 'التاريخ',
      },
      tools: {
        en: 'Tools',
        ar: 'الأدوات',
      },
      variants: {
        en: 'Variants',
        ar: 'الأنواع',
      },
      exteriorColor: {
        en: 'Exterior Color',
        ar: 'لون الهيكل',
      },
      interiorColor: {
        en: 'Interior Color',
        ar: 'ألوان المقصورة',
      },
      features: {
        en: 'Features',
        ar: 'الميزات',
      },
      step: {
        en: 'Step',
        ar: 'خطوة',
      },
      openPanel: {
        en: 'Open Panel',
        ar: 'فتح اللوحة',
      },
      closePanel: {
        en: 'Close Panel',
        ar: 'إغلاق اللوحة',
      },
    },
    landscape: {
      message: {
        en: 'Please rotate your device to portrait mode',
        ar: 'يرجى تدوير جهازك إلى الوضع الرأسي',
      },
      subtitle: {
        en: 'For the best experience, please turn your device upright.',
        ar: 'للحصول على أفضل تجربة، يرجى إمساك جهازك بشكل رأسي.',
      },
    },
    cameraViewToggle: {
      ext: {
        en: 'Ext',
        ar: 'خارجي',
      },
      int: {
        en: 'Int',
        ar: 'داخلي',
      },
      dualTone: {
        en: 'Dual-tone',
        ar: 'لون مزدوج',
      },
    },
    bottomDrawer: {
      variants: {
        en: 'Variants',
        ar: 'الأنواع',
      },
      interactions: {
        en: 'Interactions',
        ar: 'التفاعلات',
      },
      accessories: {
        en: 'Options & Accessories',
        ar: 'الإكسسوارات',
      },
      features: {
        en: 'Features',
        ar: 'الميزات',
      },
      highlights: {
        en: 'Highlights',
        ar: 'الميزات البارزة',
      },
      moreOptions: {
        en: 'More Options',
        ar: 'المزيد من الخيارات',
      },
      colorSelector: {
        en: 'Color Selector',
        ar: 'محدد اللون',
      },
      exteriorColor: {
        en: 'Exterior Color',
        ar: 'لون الهيكل',
      },
      interiorColor: {
        en: 'Interior Color',
        ar: 'لون المقصورة',
      },
      wheels: {
        en: 'Wheels',
        ar: 'العجلات',
      },
      roofs: {
        en: 'Roofs',
        ar: 'الأسقف',
      },
      performance: {
        en: 'Performance',
        ar: 'الأداء',
      },
      packages: {
        en: 'Packages',
        ar: 'الحزم',
      },
      chargingOptions: {
        en: 'Charging Options',
        ar: 'خيارات الشحن',
      },
      safetyServices: {
        en: 'Safety & Services',
        ar: 'السلامة والخدمات',
      },
      expand: {
        en: 'Expand',
        ar: 'توسيع',
      },
      minimize: {
        en: 'Minimize',
        ar: 'تصغير',
      },
      freeRoamView: {
        en: 'Free Roam View',
        ar: 'عرض التجوال الحر',
      },
      featureView: {
        en: 'Featured View',
        ar: 'العرض المميز',
      },
    },
    qr: {
      scan: {
        en: 'Scan QR',
        ar: 'امسح رمز الاستجابة السريعة',
      },
      scanWithPhone: {
        en: 'Scan with your phone to view in your Space',
        ar: 'امسح باستخدام هاتفك لعرضه في مساحتك',
      },
    },
    share: {
      title: {
        en: 'Share Configuration',
        ar: 'مشاركة التكوين',
      },
      description: {
        en: 'Share your vehicle configuration with others',
        ar: 'شارك تكوين سيارتك مع الآخرين',
      },
      twitterTitle: {
        en: 'Check out my Cadillac Lyriq configuration!',
        ar: 'شاهد تكوين سيارتي شيفروليه ترافيرس!',
      },
      whatsappTitle: {
        en: 'Check out my Cadillac Lyriq configuration!',
        ar: 'شاهد تكوين سيارتي شيفروليه ترافيرس!',
      },
      linkedinTitle: {
        en: 'My Cadillac Lyriq Configuration',
        ar: 'تكويني لسيارة شيفروليه ترافيرس',
      },
      linkedinSummary: {
        en: 'Check out my custom Cadillac Lyriq configuration!',
        ar: 'شاهد تكويني المخصص لسيارة شيفروليه ترافيرس!',
      },
      emailSubject: {
        en: 'Check out my Cadillac Lyriq Configuration',
        ar: 'شاهد تكوين سيارتي شيفروليه ترافيرس',
      },
      emailBody: {
        en: 'I configured my dream Cadillac Lyriq. Take a look:',
        ar: 'قمت بتكوين سيارة شيفروليه ترافيرس المثالية. ألق نظرة:',
      },
      linkLabel: {
        en: 'Or copy link:',
        ar: 'أو انسخ الرابط:',
      },
      copied: {
        en: 'Copied',
        ar: 'تم نسخ الرابط إلى الحافظة!',
      },
      copy: {
        en: 'Copy',
        ar: 'نسخ',
      },
      saveAndShare: {
        en: 'Save & Share',
        ar: 'حفظ ومشاركة',
      },
      copyLink: {
        en: 'Copy link',
        ar: 'نسخ الرابط',
      },
      facebook: {
        en: 'Facebook',
        ar: 'Facebook',
      },
      twitter: {
        en: 'Twitter',
        ar: 'Twitter',
      },
      gmail: {
        en: 'Gmail',
        ar: 'Gmail',
      },
      whatsapp: {
        en: 'Whatsapp',
        ar: 'Whatsapp',
      },
    },
    topBar: {
      matchingVehiclesFound: {
        en: 'Matching Vehicles Found',
        ar: 'تم العثور على المركبات المطابقة',
      },
    },
    bottomBar: {
      priceLabel: {
        en: 'ex showroom price',
        ar: 'سعر صالة العرض',
      },
      leads: {
        en: 'Save',
        ar: 'حفظ',
      },
      findLocalMatches: {
        en: 'Find Local Matches',
        ar: 'اعثر على التطابقات المحلية',
      },
      save: {
        en: 'Save',
        ar: 'حفظ',
      },
      item: {
        en: 'Item',
        ar: 'العنصر',
      },
      items: {
        en: 'Items',
        ar: 'عناصر',
      },
      totalToPay: {
        en: 'Total to pay',
        ar: 'الإجمالي للدفع',
      },
      bookTestDrive: {
        en: 'Save',
        ar: 'حفظ',
      },
      requestQuote: {
        en: 'Request a Quote',
        ar: 'طلب عرض سعر',
      },
      downloadBrochure: {
        en: 'Download Brochure',
        ar: 'تحميل الكتيب',
      },
    },
    priceSummary: {
      title: {
        en: 'Price Summary',
        ar: 'ملخص السعر',
      },
      enterLocation: {
        en: 'Enter Location',
        ar: 'أدخل الموقع',
      },
      selectCountry: {
        en: 'Location',
        ar: 'الموقع',
      },
      selectCity: {
        en: 'Location',
        ar: 'الموقع',
      },
      note: {
        en: 'Note : Prices might vary by state and all prices listed are on road price, T&C apply.',
        ar: 'ملاحظة: قد تختلف الأسعار حسب الولاية وجميع الأسعار المذكورة هي الأسعار على الطريق، تطبق الشروط والأحكام.',
      },
      breakdown: {
        en: 'Price Breakdown',
        ar: 'تفصيل السعر',
      },
      color: {
        en: 'Color',
        ar: 'اللون',
      },
      variant: {
        en: 'Variant',
        ar: 'الفئة',
      },
      included: {
        en: 'Included',
        ar: 'مشمول',
      },
      total: {
        en: 'Total',
        ar: 'الإجمالي',
      },
    },
    featureDialog: {
      playPause: {
        en: 'Play-Pause',
        ar: 'تشغيل-إيقاف',
      },
    },
    selectDealership: {
      title: {
        en: 'Select a Dealer',
        ar: 'اختر وكيلاً',
      },
      subtitle: {
        en: 'Please select a dealer to find local matches similar to your configuration',
        ar: 'يرجى اختيار وكيل للعثور على سيارات مطابقة لتكوينك في منطقتك',
      },
      city: {
        en: 'UAE',
        ar: 'الإمارات',
      },
      dealersIn: {
        en: 'Dealers in',
        ar: 'وكلاء في',
      },
      dealersInCity: {
        en: 'Dealers in {{city}}',
        ar: 'وكلاء في {{city}}',
      },
      availableInLocations: {
        en: 'Available in {{count}} locations of {{city}}',
        ar: 'متوفرة في {{count}} مواقع في {{city}}',
      },
      proceed: {
        en: 'Select Dealer and Proceed to Configurator',
        ar: 'اختر الوكيل وتابع إلى المتكون',
      },
    },
    countryNames: {
      UAE: {
        en: 'UAE',
        ar: 'الإمارات',
      },
      KSA: {
        en: 'KSA',
        ar: 'السعودية',
      },
      KW: {
        en: 'Kuwait',
        ar: 'الكويت',
      },
      default: {
        en: 'UAE',
        ar: 'الإمارات',
      },
    },
    localMatches: {
      exterior: {
        en: 'Exterior',
        ar: 'خارجي',
      },
      interior: {
        en: 'Interior',
        ar: 'داخلي',
      },
      dualTone: {
        en: 'Dual-tone',
        ar: 'لون مزدوج',
      },
      yourConfiguration: {
        en: 'Current Configuration',
        ar: 'التكوين الحالي',
      },
      viewConfiguration: {
        en: 'View your configuration details',
        ar: 'عرض تفاصيل تكوينك',
      },
      yourBuildDetails: {
        en: 'Your Build Details',
        ar: 'تفاصيل بنائك',
      },
      options: {
        en: 'Options',
        ar: 'الخيارات',
      },
      save: {
        en: 'Save',
        ar: 'حفظ',
      },
      dealersIn: {
        en: 'Dealers in',
        ar: 'وكلاء في',
      },
      changeDealer: {
        en: 'Change Dealer',
        ar: 'تغيير الوكيل',
      },
      carsAvailable: {
        en: 'Cars Available at the Selected Dealership',
        ar: 'السيارات المتوفرة لدى الوكيل المختار',
      },
      matchesFound: {
        en: 'Matches found',
        ar: 'تطابق تم العثور عليه',
      },
      startingFrom: {
        en: 'Starting from',
        ar: 'يبدأ من',
      },
      getQuote: {
        en: 'Proceed',
        ar: 'احصل على عرض سعر',
      },
      viewDifferences: {
        en: 'View Differences',
        ar: 'عرض الاختلافات',
      },
      match: {
        en: 'Match',
        ar: 'تطابق',
      },
      closestMatchFound: {
        en: 'Closest Match Found',
        ar: 'أقرب تطابق تم العثور عليه',
      },
      buildDetails: {
        en: 'Build Details',
        ar: 'تفاصيل التكوين',
      },
      trim: {
        en: 'Trim',
        ar: 'الفئة',
      },
      matchScoreTooltip: {
        en: 'This score indicates how closely this vehicle matches your configuration.',
        ar: 'يشير هذا النتيجة إلى مدى تطابق هذه السيارة مع تكوينك.',
      },
      exactMatch: {
        en: 'Exact Match',
        ar: 'تطابق تام',
      },
      similarMatch: {
        en: 'Close Match',
        ar: 'تطابق قريب',
      },
      availableNow: {
        en: 'Available Now',
        ar: 'متوفر الآن',
      },
      whatYouConfigured: {
        en: 'What you configured',
        ar: 'ما قمت بتكوينه',
      },
      matchingInStock: {
        en: 'Matching vehicle available in stock',
        ar: 'مركبة مطابقة متوفرة في المخزون',
      },
      noMatches: {
        en: 'No matching vehicles found in your area.',
        ar: 'لم يتم العثور على سيارات مطابقة في منطقتك.',
      },
      closeMatchDetails: {
        en: 'Close Match Details',
        ar: 'تفاصيل التطابق القريب',
      },
      matchesYourSelection: {
        en: 'Matches your selection',
        ar: 'يطابق اختيارك',
      },
      sameAsWhatYouSelected: {
        en: 'Same as what you selected',
        ar: 'نفس ما اخترته',
      },
      replacesYourSelection: {
        en: 'Replaces your selection',
        ar: 'يستبدل اختيارك',
      },
      selectionNotIncluded: {
        en: 'Selection not included',
        ar: 'الاختيار غير مشمول',
      },
      includedInThisVehicle: {
        en: 'Included in this vehicle',
        ar: 'مشمول في هذه المركبة',
      },
      additionalFeatureAlreadyIncluded: {
        en: 'Additional feature already included',
        ar: 'ميزة إضافية مشمولة بالفعل',
      },
      stockDisclaimer: {
        en: 'Available stock may include extra features not selected in your original configuration.',
        ar: 'قد تشمل المركبات المتاحة ميزات إضافية لم تختارها في تكوينك الأصلي.',
      },
      additionalInstalledOptionsLegend: {
        en: 'Included in this vehicle',
        ar: 'مشمول في هذه المركبة',
      },
      vehicleDetails: {
        en: 'Vehicle Details',
        ar: 'تفاصيل المركبة',
      },
      installedOptionsTitle: {
        en: 'Comes with vehicle',
        ar: 'يأتي مع المركبة',
      },
      additionalInstalledOptionsSubtitle: {
        en: 'Additional Installed Options:',
        ar: 'الخيارات المركبة الإضافية:',
      },
      specTrim: {
        en: 'Trim',
        ar: 'الفئة',
      },
      specExteriorColor: {
        en: 'Exterior Color',
        ar: 'اللون الخارجي',
      },
      specInteriorColor: {
        en: 'Interior Color',
        ar: 'اللون الداخلي',
      },
      specWheels: {
        en: 'Wheels',
        ar: 'العجلات',
      },
      specPackage: {
        en: 'Package',
        ar: 'الحزمة',
      },
      title: {
        en: 'Configurations Found',
        ar: 'تكوينات تم العثور عليها',
      },
      message: {
        en: 'We Found a few local matches similar to your current configuration. Click to view Matches',
        ar: 'وجدنا بعض التطابقات المحلية المشابهة لتكوينك الحالي. انقر لعرض التطابقات',
      },
    },
    trim: {
      selection: {
        en: 'Trim Selection',
        ar: 'اختيار الفئة',
      },
    },
    paymentPage: {
      exteriorColor: {
        en: 'Exterior Color',
        ar: 'لون الهيكل',
      },
      interiorColor: {
        en: 'Interior Color',
        ar: 'لون المقصورة',
      },
      accessory: {
        en: 'Accessory',
        ar: 'ملحق',
      },
      closePayment: {
        en: 'Close',
        ar: 'إغلاق',
      },
    },
    packageModal: {
      includes: {
        en: 'Includes:',
        ar: 'يشمل:',
      },
      of: {
        en: 'of',
        ar: 'من',
      },
      totalPriceChange: {
        en: 'Total Price Change:',
        ar: 'إجمالي تغيير السعر:',
      },
      item: {
        en: 'Item',
        ar: 'غرض',
      },
      items: {
        en: 'Items',
        ar: 'أغراض',
      },
      addPackage: {
        en: 'Add Package',
        ar: 'إضافة الحزمة',
      },
      noThanks: {
        en: 'No Thanks',
        ar: 'لا شكراً',
      },
      alreadyApplied: {
        en: 'Already Applied',
        ar: 'مُطبّق بالفعل',
      },
    },
    changesRequired: {
      en: 'Changes Required',
      ar: 'التغييرات المطلوبة',
    },
    changesRequiredDescription: {
      en: 'The package you selected conflicts with your current selections. Please review the changes below.',
      ar: 'الحزمة التي اخترتها تتعارض مع اختياراتك الحالية. يرجى مراجعة التغييرات أدناه.',
    },
    itemsThatWillBeRemoved: {
      en: 'Items that will be removed',
      ar: 'العناصر التي سيتم إزالتها',
    },
    itemsThatWillBeAdded: {
      en: 'Items that will be added',
      ar: 'العناصر التي سيتم إضافتها',
    },
    applyChanges: {
      en: 'Apply Changes',
      ar: 'تطبيق التغييرات',
    },
    previewFeature: {
      en: 'Preview Feature',
      ar: 'معاينة الميزة',
    },
    exploreFeatures: {
      en: 'Explore Features',
      ar: 'استكشف الميزات',
    },
    buildAndPrice: {
      en: 'Build & Price',
      ar: 'البناء والسعر',
    },
    features: {
      preview: {
        en: 'Preview Feature',
        ar: 'معاينة المميزات',
      },
      playAll: {
        en: 'PLAY ALL',
        ar: 'تشغيل الكل',
      },
      title: {
        en: 'Features',
        ar: 'المميزات',
      },
    },
    header: {
      localMatches: {
        en: 'Local\nMatches',
        ar: 'التطابقات\nالمحلية',
      },
      matchingVehicles: {
        en: 'Matching vehicles',
        ar: 'مركبات مطابقة',
      },
      configurationsFound: {
        en: 'Configurations Found',
        ar: 'تكوينات تم العثور عليها',
      },
    },
    configPanel: {
      playAll: {
        en: 'Play All',
        ar: 'تشغيل الكل',
      },
      pause: {
        en: 'Pause',
        ar: 'إيقاف مؤقت',
      },
      exteriorColors: {
        en: 'Exterior Colors',
        ar: 'ألوان الهيكل الخارجي',
      },
      interiorColors: {
        en: 'Interior Colors',
        ar: 'ألوان الداخلية',
      },
      roofOptions: {
        en: 'Roof Options',
        ar: 'خيارات السقف',
      },
      interiorOptions: {
        en: 'Interior Options',
        ar: 'خيارات الداخلية',
      },
      exteriorAccessories: {
        en: 'Exterior Accessories',
        ar: 'إكسسوارات خارجية',
      },
      noAdditionalOptions: {
        en: 'No additional options available for the selected trim.',
        ar: 'لا توجد خيارات إضافية متاحة للفئة المختارة.',
      },
      noFilters: {
        en: 'No filters available.',
        ar: 'لا توجد فلاتر متاحة.',
      },
      noVariantsMatch: {
        en: 'No trims match the selected filters.',
        ar: 'لا توجد فئات تطابق الفلاتر المختارة.',
      },
      configurationBreakdown: {
        en: 'Configuration Breakdown',
        ar: 'تفاصيل التكوين',
      },
      yourConfiguration: {
        en: 'Your Configuration',
        ar: 'تكوينك',
      },
      trim: {
        en: 'Trim',
        ar: 'الفئة',
      },
      total: {
        en: 'Total',
        ar: 'الإجمالي',
      },
    },
    conflictDialog: {
      description: {
        en: 'In order to apply this, the following item(s) will need to be removed due to compatibility constraints. Please review the item(s) below and proceed with your change.',
        ar: 'لتطبيق هذا، يجب إزالة العناصر التالية بسبب قيود التوافق. يرجى مراجعة العناصر أدناه والمتابعة بتغييرك.',
      },
    },
    viewport: {
      changeBackground: {
        en: 'Change Background',
        ar: 'تغيير الخلفية',
      },
      selectLanguage: {
        en: 'Switch Language (عربي)',
        ar: 'تبديل اللغة (EN)',
      },
      menu: {
        en: 'Menu',
        ar: 'القائمة',
      },
      exploreFeatures: {
        en: 'Explore Features',
        ar: 'استكشف الميزات',
      },
    },
  },
  dealers: {
    UAE: [
      {
        id: 0,
        country: 'UAE',
        value: '',
        dealerId: '122255',
        dealerName: 'Al Ghandi Auto',
        label: {
          en: 'Al Ghandi Auto - Al Quoz 3 Industrial Area Sheikh Zayed Road, Dubai',
          ar: 'الكندي للسيارات - القوز 3، المنطقة الصناعية، طريق الشيخ زايد، دبي',
        },
      },
      {
        id: 1,
        country: 'UAE',
        value: '',
        dealerId: '256427',
        dealerName: 'Al Ghandi Auto',
        label: {
          en: 'Al Ghandi Auto - Double Cola Road, Sharjah',
          ar: 'الكندي للسيارات - طريق دبل كولا، الشارقة',
        },
      },
      {
        id: 2,
        country: 'UAE',
        value: '',
        dealerId: '256428',
        dealerName: 'Al Ghandi Auto',
        label: {
          en: 'Al Ghandi Auto - Madhab Mohammad Bin Mattar Street, Fujairah',
          ar: 'الغاندي للسيارات - شارع مذهب محمد بن مطر، الفجيرة',
        },
      },
      {
        id: 3,
        country: 'UAE',
        value: '',
        dealerId: '256429',
        dealerName: 'Al Ghandi Auto',
        label: {
          en: 'Al Ghandi Auto - Al Nadiyah Sheikh Mohammed Bin Salem Road, Ras Al Khaimah',
          ar: 'الكندي للسيارات - الندية طريق الشيخ محمد بن سالم، رأس الخيمة',
        },
      },
      {
        id: 4,
        country: 'UAE',
        value: '',
        dealerId: '301597',
        dealerName: 'Al Ghandi Auto',
        label: {
          en: 'Al Ghandi Auto - Al Itihad Road - showroom , Al Khabaisi, Deira, Dubai',
          ar: 'الكندي للسيارات - طريق الاتحاد - صالة عرض , الخبيصي, ديرة, دبي',
        },
      },
      {
        id: 8,
        country: 'UAE',
        value: '',
        dealerId: '334821',
        dealerName: 'Al Ghandi Auto',
        label: {
          en: 'Al Ghandi - Mirdiff City Center',
          ar: 'الغاندي - مردف سيتي سنتر',
        },
      },
      {
        id: 5,
        country: 'UAE',
        value: '',
        dealerId: '321719',
        dealerName: '',
        label: {
          en: 'Al Ghandi Auto - City Walk Boulevard - Happiness Street, Dubai',
          ar: 'الكندي للسيارات - بوليفارد سيتي ووك - شارع السعادة، دبي',
        },
      },
      {
        id: 6,
        country: 'UAE',
        value: '',
        dealerId: '311027',
        dealerName: '',
        label: {
          en: 'Al Ghandi Auto - Umm Al Nar, Al Ain Road, Abu Dhabi',
          ar: 'الكندي للسيارات - أم النار، طريق العين، أبوظبي',
        },
      },
      {
        id: 7,
        country: 'UAE',
        value: '',
        dealerId: '315694',
        dealerName: '',
        label: {
          en: 'Al Ghandi Auto - Al Waha Building, Al Ain',
          ar: 'الكندي للسيارات - مبنى الواحة، العين',
        },
      },
    ],
    KSA: [
      {
        id: 0,
        country: 'KSA',
        value: '',
        dealerId: '307261',
        dealerName: 'Aljomaih Auto',
        label: {
          en: 'Aljomaih Auto - Al Aziziyah Madina Road, Jeddah',
          ar: 'الجميح للسيارات - العزيزية طريق المدينة، جدة',
        },
      },
      {
        id: 1,
        country: 'KSA',
        value: '',
        dealerId: '164034',
        dealerName: 'Aljomaih Auto',
        label: {
          en: 'Aljomaih Auto - Hail City King Abdullah Road. Hail',
          ar: 'الجميح للسيارات - مدينة حائل طريق الملك عبدالله. يشيد',
        },
      },
      {
        id: 2,
        country: 'KSA',
        value: '',
        dealerId: '164035',
        dealerName: 'Aljomaih Auto',
        label: {
          en: 'Aljomaih Auto - Alshlhoub King Abdullah Road, Al Jouf',
          ar: 'الجميح للسيارات - الشلهوب طريق الملك عبدالله، الجوف',
        },
      },
      {
        id: 3,
        country: 'KSA',
        value: '',
        dealerId: '307271',
        dealerName: 'Aljomaih Auto',
        label: {
          en: 'Aljomaih Auto - Qassim King Abdulaziz Road, Buraidah',
          ar: 'الجميح للسيارات - القصيم طريق الملك عبد العزيز، بريدة',
        },
      },
      {
        id: 4,
        country: 'KSA',
        value: '',
        dealerId: '260921',
        dealerName: 'Aljomaih Auto',
        label: {
          en: 'Aljomaih Auto - Al Jededah Road, Arar',
          ar: 'الجميح للسيارات - طريق الجديدة، عرعر',
        },
      },
      {
        id: 5,
        country: 'KSA',
        value: '',
        dealerId: '318928',
        dealerName: '',
        label: {
          en: 'Aljomaih Auto - King Fahad Road, Dammam',
          ar: 'الجميح للسيارات - طريق الملك فهد، الدمام',
        },
      },
      {
        id: 6,
        country: 'KSA',
        value: '',
        dealerId: '213142',
        dealerName: '',
        label: {
          en: 'Aljomaih Auto - Rawdah Khurais Road, Riyadh',
          ar: 'الجميح للسيارات - طريق الروضة خريص، الرياض',
        },
      },
      {
        id: 7,
        country: 'KSA',
        value: '',
        dealerId: '307267',
        dealerName: '',
        label: {
          en: 'Aljomaih Auto - Malibari Al Madina Road, Jeddah',
          ar: 'الجميح للسيارات - طريق ماليباري المدينة، جدة',
        },
      },
    ],
    KW: [
      {
        id: 0,
        country: 'KW',
        value: '',
        dealerId: '122620',
        dealerName: 'Alamana Auto',
        label: {
          en: 'Alamana Auto - Al Rai 4th Ring Road, Al Rai',
          ar: 'الامانة للسيارات - الري الطريق الدائري الرابع، الري',
        },
      },
      {
        id: 1,
        country: 'KW',
        value: '',
        dealerId: '174979',
        dealerName: 'Alamana Auto',
        label: {
          en: 'Alamana Auto - Fahaheel Industrial Area Dabbous Street, Fahaheel',
          ar: 'الامانة للسيارات - الفحيحيل الصناعية شارع الدبوس، الفحيحيل',
        },
      },
    ],
    BHD: [
      {
        id: 0,
        country: 'BHD',
        value: 'BHD',
        dealerId: '122215',
        dealerName: 'National Motor Company W.L.L',
        label: {
          en: 'National Motor Company W.L.L, Al Qarya 475 Shk. Jaber Al Subah Hwy',
          ar: 'الشركة الوطنية للسيارات ذ.م.م، القرية 475 شارع الشيخ. طريق جابر الصباح',
        },
      },
    ],
    LBP: [
      {
        id: 0,
        country: 'LBP',
        value: 'LBP',
        dealerId: '122226',
        dealerName: 'IMPEX',
        label: {
          en: 'IMPEX -  Badaro, Near the National Museum , Beirut',
          ar: 'إمبكس - بدارو، بالقرب من المتحف الوطني، بيروت',
        },
      },
    ],
    OMR: [
      {
        id: 0,
        country: 'OMR',
        value: 'OMR',
        dealerId: '259952',
        dealerName: 'Oman Trading Establisment',
        label: {
          en: 'Oman Trading Establisment LLC, Ghalla Al Maardh Street',
          ar: 'مؤسسة عمان التجارية ش.م.م، شارع غلا المعرض',
        },
      },
    ],
    QAR: [
      {
        id: 0,
        country: 'QAR',
        value: 'QAR',
        dealerId: '122234',
        dealerName: 'Mannai Trading Company',
        label: {
          en: 'Mannai Trading Company W.L.L., Salwa E Industrial Street',
          ar: 'شركة مناعي التجارية ذ.م.م، شارع سلوى إي الصناعي',
        },
      },
    ],
    JOD: [
      {
        id: 0,
        country: 'JOD',
        value: 'JOD',
        dealerId: '189129',
        dealerName: 'Leading Vehicles',
        label: {
          en: 'The Leading Vehicles Co. Ltd, Al Rawabi',
          ar: 'شركة المركبات الرائدة المحدودة، الروابي',
        },
      },
    ],
    default: [
      {
        id: 0,
        country: 'UAE',
        value: '',
        dealerId: '122255',
        dealerName: 'Al Ghandi Auto',
        label: {
          en: 'Al Ghandi Auto - Al Quoz 3 Industrial Area Sheikh Zayed Road, Dubai',
          ar: 'الكندي للسيارات - القوز 3، المنطقة الصناعية، طريق الشيخ زايد، دبي',
        },
      },
      {
        id: 1,
        country: 'UAE',
        value: '',
        dealerId: '256427',
        dealerName: 'Al Ghandi Auto',
        label: {
          en: 'Al Ghandi Auto - Double Cola Road, Sharjah',
          ar: 'الكندي للسيارات - طريق دبل كولا، الشارقة',
        },
      },
      {
        id: 2,
        country: 'UAE',
        value: '',
        dealerId: '256428',
        dealerName: 'Al Ghandi Auto',
        label: {
          en: 'Al Ghandi Auto - Madhab Mohammad Bin Mattar Street, Fujairah',
          ar: 'الغاندي للسيارات - شارع مذهب محمد بن مطر، الفجيرة',
        },
      },
      {
        id: 3,
        country: 'UAE',
        value: '',
        dealerId: '256429',
        dealerName: 'Al Ghandi Auto',
        label: {
          en: 'Al Ghandi Auto - Al Nadiyah Sheikh Mohammed Bin Salem Road, Ras Al Khaimah',
          ar: 'الكندي للسيارات - الندية طريق الشيخ محمد بن سالم، رأس الخيمة',
        },
      },
      {
        id: 4,
        country: 'UAE',
        value: '',
        dealerId: '301597',
        dealerName: 'Al Ghandi Auto',
        label: {
          en: 'Al Ghandi Auto - Al Itihad Road - showroom , Al Khabaisi, Deira, Dubai',
          ar: 'الكندي للسيارات - طريق الاتحاد - صالة عرض , الخبيصي, ديرة, دبي',
        },
      },
      {
        id: 8,
        country: 'UAE',
        value: '',
        dealerId: '334821',
        dealerName: 'Al Ghandi Auto',
        label: {
          en: 'Al Ghandi - Mirdiff City Center',
          ar: 'الغاندي - مردف سيتي سنتر',
        },
      },
      {
        id: 5,
        country: 'UAE',
        value: '',
        dealerId: '321719',
        dealerName: '',
        label: {
          en: 'Al Ghandi Auto - City Walk Boulevard - Happiness Street, Dubai',
          ar: 'الكندي للسيارات - بوليفارد سيتي ووك - شارع السعادة، دبي',
        },
      },
      {
        id: 6,
        country: 'UAE',
        value: '',
        dealerId: '311027',
        dealerName: '',
        label: {
          en: 'Al Ghandi Auto - Umm Al Nar, Al Ain Road, Abu Dhabi',
          ar: 'الكندي للسيارات - أم النار، طريق العين، أبوظبي',
        },
      },
      {
        id: 7,
        country: 'UAE',
        value: '',
        dealerId: '315694',
        dealerName: '',
        label: {
          en: 'Al Ghandi Auto - Al Waha Building, Al Ain',
          ar: 'الكندي للسيارات - مبنى الواحة، العين',
        },
      },
    ],
  },
  featureFlags: {
    showPriceInPresentation: false,
    showLogo: true,
    showSpecDetailsButton: true,
    useChevroletFont: false,
    showLocalMatches: true,
    showDisclaimerInPresentation: false,
    // Per-item pricing (card / MODEL / SUMMARY price lines, colours in the total)
    // is opt-in; this project keeps the header-only pricing it has always had.
    showPrices: false,
    showDisplayCarPrice: false, // Cadillac: no MSRP anywhere in the UI
    showCart: false,
  },
  config: {
    // Display currency + USD conversion factor per region, keyed by the
    // `country` URL parameter (local = usd * fx). Prices were re-derived
    // from the AED figure: usd = AED / 3.68, then usd * fx per country.
    regions: {
      KSA: { label: 'KSA', currency: 'SAR', fx: 3.75 },
      UAE: { label: 'UAE', currency: 'AED', fx: 3.68 },
      OMR: { label: 'Oman', currency: 'OMR', fx: 0.39 },
      BHD: { label: 'Bahrain', currency: 'BHD', fx: 0.38 },
      QAR: { label: 'Qatar', currency: 'QAR', fx: 3.64 },
      JOD: { label: 'Jordan', currency: 'JOD', fx: 0.71 },
      KW: { label: 'Kuwait', currency: 'KWD', fx: 0.31 },
      LBP: { label: 'Lebanon', currency: 'USD', fx: 1 }, // GM quotes Lebanon in USD
      IQD: { label: 'Iraq', currency: 'USD', fx: 1 }, // priced in USD, like Lebanon
      default: { label: 'UAE', currency: 'AED', fx: 3.68 },
    },
    // Lead-side model name — 1.0's `config.model`, which is a lead label
    // distinct from 2.0's `config.model` (the models[] key).
    leadModel: 'lyriq',
    // Dial code (prefixed onto mobile_number) and Salesforce lead country
    // code (metadome_country_code), keyed by the `country` URL parameter.
    countryCodes: {
      UAE: {
        code: '+971',
        leadCode: 'AE',
      },
      KSA: {
        code: '+966',
        leadCode: 'SA',
      },
      KW: {
        code: '+965',
        leadCode: 'KW',
      },
      BHD: {
        code: '+973',
        leadCode: 'BH',
      },
      IQD: {
        code: '+964',
        leadCode: 'IQ',
      },
      LBP: {
        code: '+961',
        leadCode: 'LB',
      },
      OMR: {
        code: '+968',
        leadCode: 'OM',
      },
      QAR: {
        code: '+974',
        leadCode: 'QA',
      },
      JOD: {
        code: '+962',
        leadCode: 'JO',
      },
      default: {
        code: '+971',
        leadCode: 'AE',
      },
    },
    model: 'Lyriq',
    modelName: 'Lyriq',
    pageTitle: 'Lyriq Configurator',
    client: 'GM',
    brand: 'cadillac',
    modelId: 'CALYRIQ',
    configurationId: 'l25zrwex1u93',
    externalAnalytics: {
      provider: 'adobe',
      brand: 'cadillac',
      modelName: 'lyriq',
      vehicleType: 'suv',
    },
    presentation: {
      video: 'videos/2369/presentation_video_1.webm',
      loadingImage: 'images/2369/sliderImage.webp',
      transitionImage: 'images/2369/transition_logo.png',
      headerLogo: 'images/2369/transition_logo.png',
      headerLogoKeepColor: true,
      headerLogoInvertInLight: true,
      // Cadillac's crest reads too small at the shared default header-logo size.
      headerLogoLarge: true,
      keyHighlights: [
        {
          index: 0,
          show: true,
          title: {
            en: '',
            ar: '',
          },
          value: {
            en: '515 HP',
            ar: '515 حصان',
          },
          unit: '',
        },
        {
          index: 1,
          show: true,
          title: {
            en: 'Torque',
            ar: 'عزم الدوران',
          },
          value: {
            en: '610 NM',
            ar: '610 نيوتن متر',
          },
          unit: '',
        },
        {
          index: 2,
          show: true,
          title: {
            en: 'Range',
            ar: 'المدى',
          },
          value: {
            en: '463 km',
            ar: '463 كم',
          },
          unit: '',
        },
      ],
    },
  },
  saveFormConfig: {
    fields: [
      {
        id: 'firstName',
        type: 'text',
        required: true,
        labelKey: 'saveDialog.labelFirstName',
        placeholderKey: 'saveDialog.placeholderFirstName',
      },
      {
        id: 'lastName',
        type: 'text',
        required: true,
        labelKey: 'saveDialog.labelLastName',
        placeholderKey: 'saveDialog.placeholderLastName',
      },
      {
        id: 'email',
        type: 'email',
        required: true,
        labelKey: 'saveDialog.labelEmail',
        placeholderKey: 'saveDialog.placeholderEmail',
        visibility: [
          'email',
          'test-drive',
          'callback',
        ],
      },
      {
        id: 'phone',
        type: 'phone',
        required: true,
        labelKey: 'saveDialog.labelMobileNumber',
        placeholderKey: 'saveDialog.placeholderMobileNumber',
        visibility: [
          'whatsapp',
          'test-drive',
          'callback',
        ],
      },
      {
        id: 'model',
        type: 'select',
        required: true,
        labelKey: 'saveDialog.labelModel',
        placeholderKey: 'saveDialog.labelModel',
        optionsSource: 'models',
        visibility: [
          'test-drive',
          'callback',
        ],
      },
      {
        id: 'dealer',
        type: 'select',
        required: true,
        labelKey: 'saveDialog.labelDealership',
        placeholderKey: 'saveDialog.placeholderSelectDealership',
        optionsSource: 'dealers',
        visibility: [
          'test-drive',
          'callback',
          'whatsapp',
          'email',
        ],
        // 1.0's save forms show the dealership dropdown but do not gate
        // submission on it; the booking forms and Save to WhatsApp
        // require it (GM needs a dealer on WhatsApp leads); Email stays optional.
        requiredFor: [
          'test-drive',
          'callback',
          'whatsapp',
        ],
      },
      {
        id: 'testDriveDate',
        type: 'date',
        required: true,
        labelKey: 'saveDialog.labelTestDriveDate',
        placeholderKey: 'saveDialog.placeholderTestDriveDate',
        visibility: [
          'test-drive',
        ],
      },
      {
        id: 'testDriveTime',
        type: 'time',
        required: true,
        labelKey: 'saveDialog.labelTestDriveTime',
        placeholderKey: 'saveDialog.placeholderTestDriveTime',
        visibility: [
          'test-drive',
        ],
      },
      {
        id: 'comments',
        type: 'textarea',
        required: false,
        labelKey: 'saveDialog.labelComments',
        placeholderKey: 'saveDialog.placeholderComments',
        visibility: [
          'test-drive',
          'callback',
        ],
      },
    ],
  },
  externalScript: {
    dev: {
      head: {
        links: [
          {
            snippet: 'https://assets.adobedtm.com/ea8c57fea068/636ca1404ad1/launch-bea0833819b4-development.min.js',
            iframe: true,
            standalone: true,
          },
          {
            snippet: 'https://assets.gm.com/responsive-iframe.min.js',
            iframe: true,
            standalone: true,
          },
          {
            snippet: 'https://cmp.osano.com/3j6afV3OqT/4661232f-0324-44a1-bbfd-07fe470494c7/osano.js',
            iframe: false,
            standalone: true,
          },
        ],
        snippets: [
          {
            snippet: '<!-- Meta Pixel Code -->\n<script>\n!function(f,b,e,v,n,t,s)\n{if(f.fbq)return;n=f.fbq=function(){n.callMethod?\nn.callMethod.apply(n,arguments):n.queue.push(argum\nents)};\nif(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version=\'2.0\';\nn.queue=[];t=b.createElement(e);t.async=!0;\nt.src=v;s=b.getElementsByTagName(e)[0];\ns.parentNode.insertBefore(t,s)}(window,\ndocument,\'script\',\n\'https://connect.facebook.net/en_US/fbevents.js\');\nfbq(\'init\', \'2090290248171811\');\nfbq(\'track\', \'PageView\');\n</script>\n<noscript><img height="1" width="1"\nstyle="display:none"\nsrc="https://www.facebook.com/tr?\nid=2090290248171811&ev=PageView&noscript=1"\n/></noscript>\n<!-- End Meta Pixel Code -->\n        ',
            iframe: true,
            standalone: true,
          },
        ],
      },
    },
    staging: {
      head: {
        links: [
          {
            snippet: 'https://assets.adobedtm.com/ea8c57fea068/636ca1404ad1/launch-77110a86d4ec-staging.min.js',
            iframe: true,
            standalone: true,
          },
          {
            snippet: 'https://assets.gm.com/responsive-iframe.min.js',
            iframe: true,
            standalone: true,
          },
          {
            snippet: 'https://cmp.osano.com/3j6afV3OqT/4661232f-0324-44a1-bbfd-07fe470494c7/osano.js',
            iframe: false,
            standalone: true,
          },
        ],
        snippets: [
          {
            snippet: '<!-- Meta Pixel Code -->\n<script>\n!function(f,b,e,v,n,t,s)\n{if(f.fbq)return;n=f.fbq=function(){n.callMethod?\nn.callMethod.apply(n,arguments):n.queue.push(argum\nents)};\nif(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version=\'2.0\';\nn.queue=[];t=b.createElement(e);t.async=!0;\nt.src=v;s=b.getElementsByTagName(e)[0];\ns.parentNode.insertBefore(t,s)}(window,\ndocument,\'script\',\n\'https://connect.facebook.net/en_US/fbevents.js\');\nfbq(\'init\', \'2090290248171811\');\nfbq(\'track\', \'PageView\');\n</script>\n<noscript><img height="1" width="1"\nstyle="display:none"\nsrc="https://www.facebook.com/tr?\nid=2090290248171811&ev=PageView&noscript=1"\n/></noscript>\n<!-- End Meta Pixel Code -->\n        ',
            iframe: true,
            standalone: true,
          },
        ],
      },
    },
    main: {
      head: {
        links: [
          {
            snippet: 'https://assets.adobedtm.com/ea8c57fea068/636ca1404ad1/launch-85b05c76e071.min.js',
            iframe: true,
            standalone: true,
          },
          {
            snippet: 'https://assets.gm.com/responsive-iframe.min.js',
            iframe: true,
            standalone: true,
          },
          {
            snippet: 'https://cmp.osano.com/3j6afV3OqT/4661232f-0324-44a1-bbfd-07fe470494c7/osano.js',
            iframe: false,
            standalone: true,
          },
        ],
        snippets: [
          {
            snippet: '<!-- Meta Pixel Code -->\n<script>\n!function(f,b,e,v,n,t,s)\n{if(f.fbq)return;n=f.fbq=function(){n.callMethod?\nn.callMethod.apply(n,arguments):n.queue.push(argum\nents)};\nif(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version=\'2.0\';\nn.queue=[];t=b.createElement(e);t.async=!0;\nt.src=v;s=b.getElementsByTagName(e)[0];\ns.parentNode.insertBefore(t,s)}(window,\ndocument,\'script\',\n\'https://connect.facebook.net/en_US/fbevents.js\');\nfbq(\'init\', \'2090290248171811\');\nfbq(\'track\', \'PageView\');\n</script>\n<noscript><img height="1" width="1"\nstyle="display:none"\nsrc="https://www.facebook.com/tr?\nid=2090290248171811&ev=PageView&noscript=1"\n/></noscript>\n<!-- End Meta Pixel Code -->\n        ',
            iframe: true,
            standalone: true,
          },
        ],
      },
    },
  },
  filterOptions: [
    {
      id: 'modelyear',
      key: 'modelyear',
      label: {
        en: 'Model Year',
        ar: 'سنة الطراز',
      },
      disabled: false,
      type: 'chip',
      options: [
        {
          id: '2025',
          label: {
            en: '2025',
            ar: '2025',
          },
          value: '2025',
          checked: false,
        },
        {
          id: '2026',
          label: {
            en: '2026',
            ar: '2026',
          },
          value: '2026',
          checked: true,
        },
      ],
    },
  ],
  styleConfig: {
    cssVariables: {
      '--default-font-family': '\'Cadillac Gothic\', \'Chevy Sans\', sans-serif',
      '--bold-font-family': '\'Cadillac Gothic\', \'Chevy Sans Medium\', sans-serif',
      '--brand-primary': '#1a1971',
      '--brand-primary-hover': '#15146a',
      '--brand-secondary': '#191897',
      '--brand-accent': '#1a1971',
      '--brand-accent-hover': '#15146a',
      '--brand-accent-light': '#e8e8f0',
      '--brand-surface': '#f3f3f3',
      '--brand-surface-alt': '#f3f4f6',
      '--brand-text': '#000000',
      '--brand-text-secondary': '#666666',
      '--brand-text-tertiary': '#bebebe',
      '--brand-border': '#e6e6e6',
      '--brand-border-selected': '#1a1971',
      '--brand-card-bg': '#ffffff',
      '--brand-toggle-gradient-from': '#1a1971',
      '--brand-toggle-gradient-to': '#191897',
      '--brand-toggle-knob-border': '#1a1971',
      '--brand-accent-secondary': '#1a1971',
      '--brand-card-radius': '0px',
      '--brand-card-radius-sm': '0px',
      '--brand-card-radius-lg': '0px',
      '--brand-button-radius': '0px',
      '--brand-button-radius-sm': '0px',
      '--brand-pill-radius': '50px',
      '--brand-cta-radius': '0px',
      // Enter-CTA fill: white with the Cadillac blue chevron, rather than
      // the default #0072ce block. Shadow keeps the label legible on it.
      '--brand-cta-arrow-bg': '#ffffff',
      '--brand-cta-arrow-fg': '#1a1971',
      '--brand-cta-shimmer': '#ffffff',
      '--enter-configurator-text-shadow': '0px 0px 12px #000000',
      '--brand-button-border': '#ffffff',
      '--brand-dialog-radius': '0px',
      '--brand-dialog-option-radius': '0px',
    },
  },
} as unknown as UIMetadataComplete;
